import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import api from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import CheckboxTreeView from "../../common/CheckboxTreeView";
import buildTree from "../../../utils/buildTree";
import {
  buildSiteSelectOptions,
  fetchStudySitesForSetup,
  normalizeStudySiteRows,
} from "../isf/isfSiteSetupService";
import { fetchEisfStudiesForBinder } from "../isf/isfDashboardService";
import utils from "../../../utils/utils";
import {
  DEFAULT_ORG_TIME_ZONE_ID,
  formatOrgWallClockDisplay,
  isOrgWallClockPast,
  toOrgWallClockComparable,
} from "../../../utils/orgTimeZoneDisplay";
import {
  fetchOrganizationSetup,
  parseOrganizationSetupResponse,
} from "../../../services/organizationSetupService";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AppointmentTimePicker from "../eplanner/appointments/AppointmentTimePicker";
import "../eplanner/appointments/AppointmentTimePicker.scss";
import "./userManagement.scss";

function filterTreeByQuery(nodes, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return nodes || [];

  const walk = (node) => {
    if (!node) return null;
    const name = String(node?.name || "").toLowerCase();
    const nameMatch = name.includes(q);

    const rawChildren = Array.isArray(node?.children) ? node.children : [];
    const filteredChildren = rawChildren.map(walk).filter(Boolean);

    if (nameMatch || filteredChildren.length > 0) {
      return {
        ...node,
        children: filteredChildren,
      };
    }
    return null;
  };

  return (nodes || []).map(walk).filter(Boolean);
}

function collectFileDocumentIdsFromCheckedIds(checkedIds) {
  return (checkedIds || [])
    .map((id) => {
      const m = String(id).match(/^[^:]+::file::(.+)$/);
      return m ? m[1] : null;
    })
    .filter(Boolean);
}

function collectFolderIdsFromCheckedIds(checkedIds) {
  return (checkedIds || [])
    .map((id) => {
      const m = String(id).match(/^[^:]+::folder::(.+)$/);
      return m ? m[1] : null;
    })
    .filter(Boolean);
}

function collectFileDocumentIdsFromTree(nodes) {
  const ids = [];
  const walk = (arr) => {
    (arr || []).forEach((n) => {
      if (n?.type === "file" && n?.id) {
        const m = String(n.id).match(/^[^:]+::file::(.+)$/);
        if (m) ids.push(m[1]);
      }
      if (Array.isArray(n?.children) && n.children.length) walk(n.children);
    });
  };
  walk(nodes);
  return ids;
}

/** Comma-separated GUIDs from API → normalized lowercase tokens */
export function parseCommaSeparatedIds(raw) {
  if (raw == null) return [];
  const s = Array.isArray(raw) ? raw.join(",") : String(raw);
  return s
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
}

function normalizeCroScopeType(raw) {
  const u = String(raw ?? "")
    .trim()
    .toUpperCase();
  if (u === "SHARE_FILES" || u === "SHARE_FOLDER" || u === "FOLDER") {
    return "SHARE_FOLDER";
  }
  if (u === "SITE") return "SITE";
  return "WHOLE_STUDY";
}

function uniqueSiteIds(values) {
  const seen = new Set();
  const out = [];
  (values || []).forEach((raw) => {
    const id = String(raw ?? "").trim();
    const key = id.toLowerCase();
    if (!id || seen.has(key)) return;
    seen.add(key);
    out.push(id);
  });
  return out;
}

function folderCheckKey(studyId, siteId, category) {
  return `${studyId}::${siteId}::${category}`;
}

function sameShareId(left, right) {
  return (
    String(left || "")
      .trim()
      .toLowerCase() ===
    String(right || "")
      .trim()
      .toLowerCase()
  );
}

function hasFolderCheckKey(checked, studyId, siteId, category) {
  if (
    Object.prototype.hasOwnProperty.call(
      checked || {},
      folderCheckKey(studyId, siteId, category),
    )
  ) {
    return true;
  }
  return Object.keys(checked || {}).some((key) => {
    const parts = String(key).split("::");
    return (
      parts.length >= 3 &&
      sameShareId(parts[0], studyId) &&
      sameShareId(parts[1], siteId) &&
      sameShareId(parts[2], category)
    );
  });
}

function dropStudyFromShareMaps(studies, scope, checked, studyId) {
  const nextStudies = (studies || []).filter(
    (row) => !sameShareId(row?.StudyId ?? row?.value, studyId),
  );
  const nextScope = {};
  Object.entries(scope || {}).forEach(([key, value]) => {
    if (sameShareId(key, studyId)) return;
    nextScope[key] = {
      ...(value || {}),
      siteIds: [...(value?.siteIds || [])],
    };
  });
  const nextChecked = { ...(checked || {}) };
  Object.keys(nextChecked).forEach((key) => {
    const prefix = String(key).split("::")[0];
    if (sameShareId(prefix, studyId)) delete nextChecked[key];
  });
  return { nextStudies, nextScope, nextChecked };
}

function removeSiteFromShareMaps(scope, checked, studyId, siteId) {
  const nextScope = {};
  Object.entries(scope || {}).forEach(([key, value]) => {
    const siteIds = uniqueSiteIds(value?.siteIds || []);
    nextScope[key] = {
      ...(value || {}),
      siteIds: sameShareId(key, studyId)
        ? siteIds.filter((id) => !sameShareId(id, siteId))
        : [...siteIds],
    };
  });
  const nextChecked = { ...(checked || {}) };
  Object.keys(nextChecked).forEach((key) => {
    const parts = String(key).split("::");
    if (
      parts.length >= 3 &&
      sameShareId(parts[0], studyId) &&
      sameShareId(parts[1], siteId)
    ) {
      delete nextChecked[key];
    }
  });
  return { nextScope, nextChecked };
}

function studyNodeKey(studyId) {
  return `study:${studyId}`;
}

function siteNodeKey(studyId, siteId) {
  return `site:${studyId}::${siteId}`;
}

function categoryNodeKey(studyId, siteId, category) {
  return `cat:${studyId}::${siteId}::${category}`;
}

function folderTreeKey(studyId, siteId, category) {
  return `${studyId}::${siteId}::${category}`;
}

function getSiteFolderTree(trees, studyId, siteId, category) {
  if (!studyId || !siteId || !category) return [];
  const studyKey = findTreeStudyKey(trees, studyId);
  if (!studyKey) return [];
  const siteKey = findTreeSiteKey(trees[studyKey], siteId);
  if (!siteKey) return [];
  return trees[studyKey][siteKey]?.[category] || [];
}

function findTreeStudyKey(trees, studyId) {
  if (!trees || studyId == null) return null;
  if (trees[studyId]) return studyId;
  const key = String(studyId).trim().toLowerCase();
  return (
    Object.keys(trees).find((k) => k.toLowerCase() === key) || null
  );
}

function findTreeSiteKey(studyTrees, siteId) {
  if (!studyTrees || siteId == null) return null;
  if (studyTrees[siteId]) return siteId;
  const key = String(siteId).trim().toLowerCase();
  return (
    Object.keys(studyTrees).find((k) => k.toLowerCase() === key) || null
  );
}

function hasSiteFolderTree(trees, studyId, siteId, category) {
  const studyKey = findTreeStudyKey(trees, studyId);
  if (!studyKey) return false;
  const siteKey = findTreeSiteKey(trees[studyKey], siteId);
  if (!siteKey) return false;
  const siteTrees = trees[studyKey][siteKey];
  return Boolean(
    siteTrees && Object.prototype.hasOwnProperty.call(siteTrees, category),
  );
}

/** True once every category tree for this site has been fetched (may be empty). */
function siteFolderTreesReady(trees, studyId, siteId, categories) {
  const cats = categories || [];
  if (!cats.length || !studyId || !siteId) return false;
  return cats.every((category) =>
    hasSiteFolderTree(trees, studyId, siteId, category),
  );
}

/** Site has at least one folder node in any of the study categories. */
function siteHasAnyFolderNodes(trees, studyId, siteId, categories) {
  return (categories || []).some(
    (category) =>
      collectFolderNodeIds(
        getSiteFolderTree(trees, studyId, siteId, category),
      ).length > 0,
  );
}

function setSiteFolderTree(prev, studyId, siteId, category, tree) {
  return {
    ...prev,
    [studyId]: {
      ...(prev?.[studyId] || {}),
      [siteId]: {
        ...(prev?.[studyId]?.[siteId] || {}),
        [category]: tree,
      },
    },
  };
}

function collectFolderNodeIds(nodes) {
  const ids = [];
  const walk = (arr) => {
    (arr || []).forEach((n) => {
      if (n?.type === "folder" && n?.id != null) ids.push(n.id);
      if (Array.isArray(n?.children) && n.children.length) walk(n.children);
    });
  };
  walk(nodes);
  return ids;
}

function idsCoverWholeTree(tree, checkedIds) {
  const all = collectFolderNodeIds(tree);
  if (!all.length) return true;
  const set = new Set(checkedIds || []);
  return all.every((id) => set.has(id));
}

function truthyFlag(value) {
  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    String(value ?? "")
      .trim()
      .toLowerCase() === "true"
  );
}

function binderHasEisf(row) {
  return truthyFlag(
    row?.HaseISFTemplate ?? row?.haseISFTemplate ?? row?.HaseISF,
  );
}

function binderHasEpsf(row) {
  return truthyFlag(
    row?.HasePSFTemplate ?? row?.hasePSFTemplate ?? row?.HasePSF,
  );
}

function binderHasAny(row) {
  if (!row) return false;
  const eisf = row.HaseISFTemplate ?? row.haseISFTemplate ?? row.HaseISF;
  const epsf = row.HasePSFTemplate ?? row.hasePSFTemplate ?? row.HasePSF;
  // Missing flags → do not assume a binder exists.
  if (eisf == null && epsf == null) return false;
  return binderHasEisf(row) || binderHasEpsf(row);
}

/**
 * Parse DocumentsXML from GetProfileDetails / SAVE_UPDATE_USER (same shape we generate).
 * Returns rows: { studyId, category: 'eISF'|'ePSF', scopeType, documentids }.
 */
export function parseCroDocumentListXml(raw) {
  if (raw == null) return null;
  const str = String(raw).trim();
  if (!str) return null;
  try {
    const wrapped = str.includes("<DocumentList")
      ? str
      : `<DocumentList>${str}</DocumentList>`;
    const doc = new DOMParser().parseFromString(wrapped, "text/xml");
    if (doc.querySelector("parsererror")) return null;
    const grantEls = [...doc.getElementsByTagName("Grant")];
    const studyEls = [...doc.getElementsByTagName("Study")];
    const source = grantEls.length ? grantEls : studyEls;
    if (!source.length) return null;
    const rows = source
      .map((el) => ({
        studyId: String(el.getAttribute("StudyId") ?? "").trim(),
        category: String(
          el.getAttribute("Category") ?? el.getAttribute("category") ?? "",
        ).trim(),
        scopeType: normalizeCroScopeType(el.getAttribute("ScopeType")),
        documentids:
          el.getAttribute("FolderIds") ??
          el.getAttribute("documentids") ??
          "",
        siteId: String(el.getAttribute("SiteId") ?? "").trim(),
        accessUntil: String(el.getAttribute("AccessUntil") ?? "").trim(),
        module: String(el.getAttribute("Module") ?? "isf").trim() || "isf",
      }))
      .filter(
        (r) =>
          r.studyId &&
          (r.category === "eISF" || r.category === "ePSF" || !r.category),
      );
    return rows.length ? rows : null;
  } catch {
    return null;
  }
}

/**
 * API shape on userResponse.data.ExternalStaffISFDocuments:
 * array of { StudyId, Category, ScopeType, DocumentIds } (Pascal or camelCase).
 * ScopeType: WHOLE_STUDY | SHARE_FOLDER (or SHARE_FILES → normalized).
 * DocumentIds: comma-separated string or array of GUIDs.
 * If ScopeType is omitted, it is inferred from whether DocumentIds has entries.
 * Returns the same row shape as parseCroDocumentListXml, or null if not structured / empty.
 */
export function parseExternalStaffIsfDocumentsStructured(raw) {
  if (raw == null) return null;
  const list = Array.isArray(raw)
    ? raw
    : typeof raw === "object"
      ? [raw]
      : null;
  if (!list?.length) return null;

  const normalizeCategory = (c) => {
    const s = String(c ?? "").trim();
    const u = s.toLowerCase();
    if (u === "eisf" || s === "eISF") return "eISF";
    if (u === "epsf" || s === "ePSF") return "ePSF";
    return null;
  };

  const docIdsToString = (docIds) => {
    if (docIds == null) return "";
    if (Array.isArray(docIds)) {
      return docIds
        .map((x) => String(x).trim())
        .filter(Boolean)
        .join(",");
    }
    return String(docIds);
  };

  const rows = [];
  for (const item of list) {
    if (!item || typeof item !== "object") continue;
    const studyId = String(
      item.StudyId ?? item.studyId ?? item.Value ?? "",
    ).trim();
    const category = normalizeCategory(item.Category ?? item.category);
    if (!studyId || !category) continue;
    const docStr = docIdsToString(
      item.FolderIds ??
        item.folderIds ??
        item.DocumentIds ??
        item.documentIds ??
        item.DocumentIdList ??
        item.FolderId ??
        item.folderId,
    );
    const ids = parseCommaSeparatedIds(docStr);
    const rawScopeAttr = item.ScopeType ?? item.scopeType;
    const hasExplicitScope =
      rawScopeAttr != null && String(rawScopeAttr).trim() !== "";
    const scopeType = hasExplicitScope
      ? normalizeCroScopeType(rawScopeAttr)
      : ids.length > 0
        ? "SHARE_FOLDER"
        : "WHOLE_STUDY";
    rows.push({
      studyId,
      category,
      scopeType,
      documentids: docStr,
      siteId: String(item.SiteId ?? item.siteId ?? "").trim(),
    });
  }
  return rows.length ? rows : null;
}

function parseJsonIfString(raw) {
  if (typeof raw !== "string") return raw;
  const text = raw.trim();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function rowsFromUnknownTable(raw) {
  const value = parseJsonIfString(raw) ?? raw;
  if (value == null) return [];
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.Table)) return value.Table;
  if (Array.isArray(value?.Rows)) {
    const cols = (value.Columns || value.columns || []).map(
      (c) => c?.ColumnName || c?.columnName || c?.name || "",
    );
    if (cols.length && Array.isArray(value.Rows[0])) {
      return value.Rows.map((values) => {
        const row = {};
        cols.forEach((name, i) => {
          if (name) row[name] = values?.[i];
        });
        return row;
      });
    }
    return value.Rows;
  }
  if (value && typeof value === "object") {
    const nested =
      value.ExternalEntityAccess ??
      value.externalEntityAccess ??
      value.ExternalEntityAccessJson;
    if (nested != null && nested !== value) {
      const nestedRows = rowsFromUnknownTable(nested);
      if (nestedRows.length) return nestedRows;
    }
    if (Array.isArray(value.Tables)) {
      for (const table of value.Tables) {
        const name = String(table?.TableName || table?.tableName || "").toLowerCase();
        if (name === "externalentityaccess") {
          const nestedRows = rowsFromUnknownTable(table);
          if (nestedRows.length) return nestedRows;
        }
      }
    }
    const hasStudy = value.StudyId ?? value.studyId;
    if (hasStudy != null && String(hasStudy).trim() !== "") return [value];
  }
  return [];
}

/** Pull live grants from GetProfileDetails DataSet / StaffRecord JSON / GetValid list. */
export function extractExternalEntityAccessFromProfile(data, staff) {
  const candidates = [
    data?.ExternalEntityAccess,
    staff?.ExternalEntityAccess,
    staff?.ExternalEntityAccessJson,
    data?.StaffRecord?.[0]?.ExternalEntityAccessJson,
    data?.staffRecord?.[0]?.ExternalEntityAccessJson,
  ];
  if (Array.isArray(data?.Tables)) {
    for (const table of data.Tables) {
      const name = String(table?.TableName || table?.tableName || "").toLowerCase();
      if (name === "externalentityaccess") candidates.push(table);
    }
  }
  if (data && typeof data === "object") {
    Object.keys(data).forEach((key) => {
      if (!/^table\d*$/i.test(key)) return;
      const table = data[key];
      const sample = Array.isArray(table) ? table[0] : table;
      if (
        sample &&
        typeof sample === "object" &&
        (sample.StudyId || sample.studyId) &&
        (sample.SiteId != null || sample.siteId != null)
      ) {
        candidates.push(table);
      }
    });
  }
  let fallback = null;
  for (const candidate of candidates) {
    const parsed = parseExternalEntityAccessRows(candidate);
    if (!parsed?.length) continue;
    const hasAudit = parsed.some((row) => row.createdByName || row.createdOn);
    if (hasAudit) return candidate;
    if (!fallback) fallback = candidate;
  }
  return fallback;
}

export async function fetchExternalEntityAccessGrants({
  organizationId,
  staffId,
}) {
  if (!organizationId || !staffId) return null;
  const response = await api.post(API_ENDPOINTS.USER.GET_EXTERNAL_ENTITY_ACCESS, {
    OrganizationId: organizationId,
    StaffId: staffId,
    Action: "GetValid",
  });
  if (Number(response?.success) !== 1) return null;
  return response.data ?? null;
}

/**
 * Audit log for Sponsor/CRO shared access (GRANT / REMOVE / UPDATE).
 * Pass staffId for one user; omit for organisation-wide log.
 */
export async function fetchExternalEntityAccessLog({
  organizationId,
  staffId = "",
}) {
  if (!organizationId) return [];
  const response = await api.post(API_ENDPOINTS.USER.GET_EXTERNAL_ENTITY_ACCESS, {
    OrganizationId: organizationId,
    StaffId: staffId || "",
    Action: "GetLog",
  });
  if (Number(response?.success) !== 1) {
    throw new Error(
      response?.message || "Could not load shared access activity log.",
    );
  }
  const raw = response.data;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.Table)) return raw.Table;
  if (Array.isArray(raw?.AccessLog)) return raw.AccessLog;
  return [];
}

function getStudyScope(scopeByStudy, studyId) {
  if (!scopeByStudy || studyId == null) return null;
  if (scopeByStudy[studyId]) return scopeByStudy[studyId];
  const key = String(studyId).trim().toLowerCase();
  const found = Object.keys(scopeByStudy).find((k) => k.toLowerCase() === key);
  return found ? scopeByStudy[found] : null;
}

function getStudySiteOptions(studySitesByStudyId, studyId) {
  if (!studySitesByStudyId || studyId == null) return [];
  if (Array.isArray(studySitesByStudyId[studyId])) {
    return studySitesByStudyId[studyId];
  }
  const key = String(studyId).trim().toLowerCase();
  const found = Object.keys(studySitesByStudyId).find(
    (k) => k.toLowerCase() === key,
  );
  return found ? studySitesByStudyId[found] || [] : [];
}

const GUID_LIKE_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function cleanStudySiteLabel(label) {
  return String(label || "")
    .replace(/\s*\(\s*0+\s*\)\s*$/, "")
    .trim();
}

function findStudySiteOption(studySitesByStudyId, studyId, siteId) {
  const wanted = String(siteId || "").trim().toLowerCase();
  if (!wanted) return null;
  const local = getStudySiteOptions(studySitesByStudyId, studyId);
  const match = (local || []).find(
    (site) => String(site?.value || "").toLowerCase() === wanted,
  );
  if (match) return match;
  return (
    Object.values(studySitesByStudyId || {})
      .flatMap((list) => list || [])
      .find((site) => String(site?.value || "").toLowerCase() === wanted) ||
    null
  );
}

function collapsePersonName(name) {
  return String(name || "").replace(/\s+/g, " ").trim();
}

const WHOLE_DAY_UI_TIME = "00:00"; // 12:00 AM in the picker = whole day
const WHOLE_DAY_END_TIME = "23:59"; // persisted AccessUntil for whole day

function pad2Clock(value) {
  return String(value).padStart(2, "0");
}

function dateToIsoLocal(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad2Clock(date.getMonth() + 1)}-${pad2Clock(date.getDate())}`;
}

function isoToLocalDate(dateIso) {
  const text = String(dateIso || "").trim();
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const next = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
  );
  return Number.isNaN(next.getTime()) ? null : next;
}

function todayIsoLocal() {
  return dateToIsoLocal(new Date());
}

function isWholeDayTime(timeHHmm) {
  const t = String(timeHHmm || "").trim();
  return !t || t === WHOLE_DAY_UI_TIME || t.startsWith("00:00");
}

/** Persist AccessUntil. 12:00 AM (or empty) → end of that org day (23:59). */
function buildAccessUntilForSave(dateIso, timeHHmm) {
  const date = String(dateIso || "").trim();
  if (!date) return "";
  if (isWholeDayTime(timeHHmm)) {
    return `${date} ${WHOLE_DAY_END_TIME}:00`;
  }
  const time = String(timeHHmm).trim();
  const withSeconds = /^\d{1,2}:\d{2}$/.test(time) ? `${time}:00` : time;
  return `${date} ${withSeconds}`;
}

/**
 * Split stored AccessUntil into UI date + time.
 * Whole-day saves (23:59) show as 12:00 AM.
 */
function splitAccessUntilForUi(raw) {
  const comparable = toOrgWallClockComparable(raw);
  if (!comparable) {
    const legacy = String(raw || "").trim();
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(legacy)) {
      const date = legacy.slice(0, 10);
      const time = legacy.slice(11, 16);
      const timeForUi = isWholeDayTime(time) || time === WHOLE_DAY_END_TIME
        ? WHOLE_DAY_UI_TIME
        : time;
      return {
        accessUntilDate: date,
        accessUntilTime: timeForUi,
        accessUntil: buildAccessUntilForSave(date, timeForUi),
      };
    }
    return { accessUntilDate: "", accessUntilTime: "", accessUntil: "" };
  }
  const [date, timePart] = comparable.split(" ");
  const time = String(timePart || "").slice(0, 5);
  const timeForUi =
    time === WHOLE_DAY_END_TIME || isWholeDayTime(time)
      ? WHOLE_DAY_UI_TIME
      : time;
  return {
    accessUntilDate: date,
    accessUntilTime: timeForUi,
    accessUntil: buildAccessUntilForSave(date, timeForUi),
  };
}

function normalizeAccessUntilForSave(scopeOrRaw) {
  if (scopeOrRaw == null) return "";
  if (typeof scopeOrRaw === "object") {
    if (scopeOrRaw.accessUntilDate) {
      return buildAccessUntilForSave(
        scopeOrRaw.accessUntilDate,
        scopeOrRaw.accessUntilTime,
      );
    }
    if (scopeOrRaw.accessUntil) {
      return splitAccessUntilForUi(scopeOrRaw.accessUntil).accessUntil;
    }
    return "";
  }
  return splitAccessUntilForUi(scopeOrRaw).accessUntil;
}

function splitHHmm(hhmm) {
  const text = String(hhmm || "").trim();
  const match = text.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return { hour: "", minute: "", ampm: "AM" };
  const hour24 = Number(match[1]);
  const minute = Number(match[2]);
  if (Number.isNaN(hour24) || Number.isNaN(minute)) {
    return { hour: "", minute: "", ampm: "AM" };
  }
  const ampm = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return { hour: String(hour12), minute: pad2Clock(minute), ampm };
}

function toHHmm(hourRaw, minuteRaw, ampm) {
  let hour = Number(hourRaw);
  const minute = Number(minuteRaw);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return "";
  if (hour < 1 || hour > 12 || minute < 0 || minute > 59) return "";
  if (ampm === "PM" && hour < 12) hour += 12;
  if (ampm === "AM" && hour === 12) hour = 0;
  return `${pad2Clock(hour)}:${pad2Clock(minute)}`;
}

function snapMinute(raw) {
  const minute = Number(raw);
  if (Number.isNaN(minute)) return "";
  const snapped = Math.min(55, Math.max(0, Math.round(minute / 5) * 5));
  return pad2Clock(snapped);
}

function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Optional Visible until: date + time on one row; default 12:00 AM = whole day. */
function VisibleUntilFields({
  dateIso,
  timeHHmm,
  disabled,
  onChange,
}) {
  const selectedDate = isoToLocalDate(dateIso);
  const effectiveTime = dateIso
    ? timeHHmm || WHOLE_DAY_UI_TIME
    : "";
  const parts = splitHHmm(effectiveTime || WHOLE_DAY_UI_TIME);
  const [hour, setHour] = useState(parts.hour);
  const [minute, setMinute] = useState(parts.minute);
  const [ampm, setAmpm] = useState(parts.ampm || "AM");

  useEffect(() => {
    const next = splitHHmm(effectiveTime || WHOLE_DAY_UI_TIME);
    setHour(next.hour);
    setMinute(next.minute);
    setAmpm(next.ampm || "AM");
  }, [effectiveTime]);

  const emit = (nextDateIso, nextTime) => {
    const timeValue = nextDateIso
      ? nextTime || WHOLE_DAY_UI_TIME
      : "";
    onChange?.({
      accessUntilDate: nextDateIso || "",
      accessUntilTime: nextDateIso ? timeValue : "",
      accessUntil: buildAccessUntilForSave(nextDateIso, timeValue),
    });
  };

  const commitTime = (nextHour, nextMinute, nextAmpm) => {
    const normalized = toHHmm(nextHour, nextMinute, nextAmpm);
    if (!normalized || !dateIso) return;
    emit(dateIso, normalized);
  };

  return (
    <label className="external-grant-col__until">
      <span>Visible until (optional)</span>
      <div className="external-visible-until">
        <DatePicker
          selected={selectedDate}
          onChange={(date) => {
            if (disabled) return;
            if (!date) {
              emit("", "");
              return;
            }
            const nextIso = dateToIsoLocal(date);
            let nextTime = timeHHmm || WHOLE_DAY_UI_TIME;
            if (
              nextIso === todayIsoLocal() &&
              nextTime &&
              !isWholeDayTime(nextTime)
            ) {
              const [hh, mm] = nextTime.split(":").map(Number);
              const now = new Date();
              if (
                hh < now.getHours() ||
                (hh === now.getHours() && mm < now.getMinutes())
              ) {
                nextTime = WHOLE_DAY_UI_TIME;
              }
            }
            if (!nextTime) nextTime = WHOLE_DAY_UI_TIME;
            emit(nextIso, nextTime);
          }}
          placeholderText="Select date"
          className="form-control form-control-sm"
          dateFormat="dd/MM/yyyy"
          isClearable={!disabled}
          disabled={disabled}
          minDate={startOfToday()}
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          onChangeRaw={(event) => event.preventDefault()}
          popperProps={{ strategy: "fixed" }}
          popperClassName="external-visible-until__date-popper"
        />
        <div className="external-visible-until__time-wrap">
          <AppointmentTimePicker
            value={effectiveTime}
            onChange={(next) => {
              if (disabled || !dateIso) return;
              emit(dateIso, next || WHOLE_DAY_UI_TIME);
            }}
            disabled={disabled || !dateIso}
            appointmentDate={dateIso || todayIsoLocal()}
            intervalMinutes={5}
            ignorePast={false}
            hidePastSlots
            fullDay
            placeholder="12:00 AM"
            aria-label="Visible until time"
            renderTrigger={({
              triggerRef,
              open,
              setOpen,
              disabled: pickerDisabled,
            }) => (
              <div
                ref={triggerRef}
                className={`external-visible-until__segments${
                  open ? " is-open" : ""
                }${!dateIso ? " is-disabled" : ""}`}
              >
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  className="external-visible-until__part"
                  placeholder="12"
                  maxLength={2}
                  disabled={pickerDisabled}
                  value={hour}
                  aria-label="Hours"
                  onChange={(e) =>
                    setHour(e.target.value.replace(/\D/g, "").slice(0, 2))
                  }
                  onBlur={() => {
                    if (hour === "") {
                      emit(dateIso, WHOLE_DAY_UI_TIME);
                      return;
                    }
                    const nextHour = String(
                      Math.min(12, Math.max(1, Number(hour) || 12)),
                    );
                    setHour(nextHour);
                    commitTime(nextHour, minute || "00", ampm || "AM");
                  }}
                />
                <span className="external-visible-until__sep">:</span>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  className="external-visible-until__part"
                  placeholder="00"
                  maxLength={2}
                  disabled={pickerDisabled}
                  value={minute}
                  aria-label="Minutes"
                  onChange={(e) =>
                    setMinute(e.target.value.replace(/\D/g, "").slice(0, 2))
                  }
                  onBlur={() => {
                    if (minute === "") {
                      emit(dateIso, WHOLE_DAY_UI_TIME);
                      return;
                    }
                    const nextMinute = snapMinute(minute);
                    setMinute(nextMinute);
                    commitTime(hour || "12", nextMinute, ampm || "AM");
                  }}
                />
                <button
                  type="button"
                  className="external-visible-until__ampm"
                  disabled={pickerDisabled}
                  aria-label="AM or PM"
                  onClick={() => {
                    const next = ampm === "PM" ? "AM" : "PM";
                    setAmpm(next);
                    commitTime(hour || "12", minute || "00", next);
                  }}
                >
                  {ampm || "AM"}
                </button>
                <button
                  type="button"
                  className="external-visible-until__clock"
                  disabled={pickerDisabled}
                  aria-label="Open time picker"
                  aria-expanded={open}
                  onClick={() => {
                    if (pickerDisabled) return;
                    setOpen((current) => !current);
                  }}
                >
                  <i className="far fa-clock" aria-hidden="true" />
                </button>
              </div>
            )}
          />
        </div>
      </div>
    </label>
  );
}

function formatAssignedOnDate(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const msMatch = raw.match(/\/Date\((-?\d+)\)\//);
  const parsed = msMatch
    ? new Date(Number(msMatch[1]))
    : utils.parseDateInput(raw) || new Date(raw);
  if (!parsed || Number.isNaN(parsed.getTime())) return "";
  // Same display as Visible until: dd/MM/yyyy hh:mm tt
  const formatted = utils.formatDateTimeDisplay(parsed);
  return formatted === "—" ? "" : formatted;
}

function assignedOnSortMs(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return 0;
  const msMatch = raw.match(/\/Date\((-?\d+)\)\//);
  const parsed = msMatch
    ? new Date(Number(msMatch[1]))
    : utils.parseDateInput(raw) || new Date(raw);
  if (!parsed || Number.isNaN(parsed.getTime())) return 0;
  return parsed.getTime();
}

function findGrantAudit(grantAudit, studyId, siteId, category) {
  const study = String(studyId || "").trim().toLowerCase();
  const site = String(siteId || "").trim().toLowerCase();
  const cat = String(category || "eISF").trim().toLowerCase();
  const exact = grantAudit.get(`${study}|${site}|${cat}`);
  if (exact) return exact;
  const siteOnly = grantAudit.get(`${study}|${site}|`);
  if (siteOnly) return siteOnly;
  for (const [key, value] of grantAudit.entries()) {
    if (key.startsWith(`${study}|${site}|`)) return value;
  }
  for (const [key, value] of grantAudit.entries()) {
    if (key.startsWith(`${study}|`)) return value;
  }
  return {};
}

function resolveAssignedSiteName(siteId, studySitesByStudyId, studyId) {
  const match = findStudySiteOption(studySitesByStudyId, studyId, siteId);
  const label = cleanStudySiteLabel(match?.label || "");
  if (label && !GUID_LIKE_RE.test(label)) return label;
  return "";
}

function mergeFolderIdCsv(a, b) {
  return uniqueSiteIds([
    ...parseCommaSeparatedIds(a),
    ...parseCommaSeparatedIds(b),
  ]).join(",");
}

/**
 * Live grants from GetProfileDetails ExternalEntityAccess (StaffExternalEntityAccess).
 * One UI row per study + site + category; FolderId rows are combined.
 */
export function parseExternalEntityAccessRows(raw) {
  const list = rowsFromUnknownTable(raw);
  if (!list.length) return null;
  const grouped = new Map();
  for (const item of list) {
    if (!item || typeof item !== "object") continue;
    const studyId = String(item.StudyId ?? item.studyId ?? "").trim();
    if (!studyId) continue;
    const siteId = String(item.SiteId ?? item.siteId ?? "").trim();
    const rawCat = String(item.Category ?? item.category ?? "").trim();
    const catLower = rawCat.toLowerCase();
    const category =
      catLower === "eisf" || rawCat === "eISF"
        ? "eISF"
        : catLower === "epsf" || rawCat === "ePSF"
          ? "ePSF"
          : rawCat || "eISF";
    const scopeType = normalizeCroScopeType(item.ScopeType ?? item.scopeType);
    const folderCsv = [
      item.FolderIds ?? item.folderIds ?? item.DocumentIds ?? item.documentIds,
      item.FolderId ?? item.folderId,
    ]
      .map((v) => String(v ?? "").trim())
      .filter(Boolean)
      .join(",");
    const accessUntil = String(item.AccessUntil ?? item.accessUntil ?? "").trim();
    const key = `${studyId.toLowerCase()}|${siteId.toLowerCase()}|${category}`;
    if (!grouped.has(key)) {
      grouped.set(key, {
        studyId,
        siteId,
        category,
        scopeType,
        documentids: "",
        accessUntil,
        createdBy: String(
          item.CreatedBy ?? item.createdBy ?? item.AssignedById ?? "",
        ).trim(),
        createdByName: collapsePersonName(
          item.CreatedByName ??
            item.createdByName ??
            item.AssignedBy ??
            item.assignedBy ??
            "",
        ),
        createdOn: String(
          item.CreatedOn ??
            item.createdOn ??
            item.AssignedOn ??
            item.assignedOn ??
            "",
        ).trim(),
      });
    }
    const slot = grouped.get(key);
    slot.documentids = mergeFolderIdCsv(slot.documentids, folderCsv);
    if (scopeType === "SHARE_FOLDER" || slot.documentids) {
      slot.scopeType = "SHARE_FOLDER";
    } else if (scopeType === "SITE") {
      slot.scopeType = "SITE";
    }
    if (accessUntil) slot.accessUntil = accessUntil;
    const createdBy = String(
      item.CreatedBy ?? item.createdBy ?? item.AssignedById ?? "",
    ).trim();
    const createdByName = collapsePersonName(
      item.CreatedByName ??
        item.createdByName ??
        item.AssignedBy ??
        item.assignedBy ??
        "",
    );
    const createdOn = String(
      item.CreatedOn ??
        item.createdOn ??
        item.AssignedOn ??
        item.assignedOn ??
        "",
    ).trim();
    if (createdBy && !slot.createdBy) slot.createdBy = createdBy;
    if (createdByName && !slot.createdByName) slot.createdByName = createdByName;
    if (createdOn && !slot.createdOn) slot.createdOn = createdOn;
  }
  const rows = [...grouped.values()];
  return rows.length ? rows : null;
}

/** Escape attribute values for DocumentsXML Grant nodes. */
export function escapeXmlAttr(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll('"', "&quot;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("'", "&apos;");
}

/**
 * Internal save rows → DocumentsXML for SAVE_UPDATE_USER.
 * Row shape: { module, studyId, siteId, category, scopeType, folderids|documentids, accessUntil }
 */
export function documentRowsToDocumentsXml(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const xmlRows = list
    .filter((r) => r && String(r.studyId ?? "").trim())
    .map(
      (r) =>
        `<Grant Module="${escapeXmlAttr(r.module || "isf")}" StudyId="${escapeXmlAttr(
          r.studyId,
        )}" SiteId="${escapeXmlAttr(r.siteId || "")}" Category="${escapeXmlAttr(
          r.category,
        )}" ScopeType="${escapeXmlAttr(r.scopeType)}" FolderIds="${escapeXmlAttr(
          r.folderids || r.documentids || "",
        )}" AccessUntil="${escapeXmlAttr(r.accessUntil || "")}" />`,
    );
  return xmlRows.length
    ? `<DocumentList>${xmlRows.join("")}</DocumentList>`
    : "";
}

/**
 * Internal save rows → ExternalStaffISFDocuments array for SAVE_UPDATE_USER.
 */
export function documentRowsToExternalStaffIsfDocuments(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return list
    .filter((r) => r && String(r.studyId ?? "").trim())
    .map((r) => ({
      Module: r.module || "isf",
      StudyId: r.studyId,
      SiteId: r.siteId || "",
      Category: r.category,
      ScopeType: r.scopeType,
      FolderIds: r.folderids || r.documentids || "",
      AccessUntil: r.accessUntil || "",
    }));
}

/**
 * GetValid / parseExternalEntityAccessRows → internal save-row shape
 * (same as buildCroDocumentRowsForSave output).
 */
export function externalEntityAccessToDocumentRows(raw) {
  const parsed = parseExternalEntityAccessRows(raw) || [];
  return parsed
    .filter((r) => r && String(r.studyId || "").trim())
    .map((r) => {
      const folderCsv = String(r.documentids || "").trim();
      const isFolder =
        r.scopeType === "SHARE_FOLDER" ||
        r.scopeType === "FOLDER" ||
        Boolean(folderCsv);
      return {
        module: "isf",
        studyId: r.studyId,
        siteId: r.siteId || "",
        category: r.category || "eISF",
        scopeType: isFolder ? "FOLDER" : "SITE",
        folderids: isFolder ? folderCsv : "",
        accessUntil: r.accessUntil || "",
      };
    });
}

/**
 * Merge delta grant rows into existing grants without wiping unrelated studies.
 * For each study in the delta: replace only sites present in the delta for that
 * study, keep other sites, then append delta rows. Studies absent from the delta
 * are unchanged. (New-study assign has no prior sites → equivalent to study replace.)
 */
export function mergeDocumentRowsWithDelta(existingRows, deltaRows) {
  const existing = Array.isArray(existingRows) ? existingRows : [];
  const delta = Array.isArray(deltaRows) ? deltaRows : [];
  if (!delta.length) return existing;

  const deltaSitesByStudy = new Map();
  delta.forEach((row) => {
    const studyKey = String(row?.studyId ?? "")
      .trim()
      .toLowerCase();
    if (!studyKey) return;
    const siteKey = String(row?.siteId ?? "")
      .trim()
      .toLowerCase();
    if (!deltaSitesByStudy.has(studyKey)) {
      deltaSitesByStudy.set(studyKey, new Set());
    }
    if (siteKey) deltaSitesByStudy.get(studyKey).add(siteKey);
  });

  const kept = existing.filter((row) => {
    const studyKey = String(row?.studyId ?? "")
      .trim()
      .toLowerCase();
    if (!studyKey || !deltaSitesByStudy.has(studyKey)) return true;
    const sites = deltaSitesByStudy.get(studyKey);
    if (!sites.size) return false;
    const siteKey = String(row?.siteId ?? "")
      .trim()
      .toLowerCase();
    return !sites.has(siteKey);
  });

  return [...kept, ...delta];
}

/** studyId (any case) → { siteIds, bySite, eISF?, ePSF? } */
function groupCroXmlRowsByStudyId(rows) {
  const map = new Map();
  for (const r of rows) {
    const key = String(r.studyId).trim().toLowerCase();
    if (!key) continue;
    if (!map.has(key)) map.set(key, { siteIds: [], bySite: {} });
    const slot = map.get(key);
    const mergeCat = (current, next) => {
      if (!next) return current;
      if (!current) return next;
      return {
        ...current,
        ...next,
        documentids: mergeFolderIdCsv(current.documentids, next.documentids),
      };
    };
    if (r.siteId) {
      slot.siteIds = uniqueSiteIds([...(slot.siteIds || []), r.siteId]);
      const siteKey = String(r.siteId).trim().toLowerCase();
      if (!slot.bySite[siteKey]) slot.bySite[siteKey] = { siteId: r.siteId };
      if (r.category === "eISF") {
        slot.bySite[siteKey].eISF = mergeCat(slot.bySite[siteKey].eISF, r);
      }
      if (r.category === "ePSF") {
        slot.bySite[siteKey].ePSF = mergeCat(slot.bySite[siteKey].ePSF, r);
      }
    }
    if (r.scopeType === "SHARE_FOLDER") slot.hasFolderScope = true;
    if (r.category === "eISF") slot.eISF = mergeCat(slot.eISF, r);
    if (r.category === "ePSF") slot.ePSF = mergeCat(slot.ePSF, r);
  }
  return map;
}

function collectNodeIdsMatchingIdSet(tree, idSet) {
  const out = [];
  const walk = (nodes) => {
    for (const n of nodes || []) {
      if (n?.id) {
        const folder = String(n.id).match(/^[^:]+::folder::(.+)$/);
        const file = String(n.id).match(/^[^:]+::file::(.+)$/);
        const guid = (folder?.[1] || file?.[1] || "").trim().toLowerCase();
        if (guid && idSet.has(guid)) out.push(n.id);
      }
      if (n?.children?.length) walk(n.children);
    }
  };
  walk(tree);
  return expandCheckedWithAncestors(out, tree);
}

function checkedIdsFromPlanRow(planRow, tree) {
  if (!planRow) return collectFolderNodeIds(tree);
  const rowScope = planRow.scopeType || "WHOLE_STUDY";
  if (
    rowScope === "WHOLE_STUDY" ||
    rowScope === "SITE" ||
    parseCommaSeparatedIds(planRow.documentids ?? "").length === 0
  ) {
    return collectFolderNodeIds(tree);
  }
  return collectNodeIdsMatchingIdSet(
    tree,
    new Set(parseCommaSeparatedIds(planRow.documentids ?? "")),
  );
}

function buildIdToNodeMapFromTree(tree) {
  const map = new Map();
  const walk = (nodes) => {
    for (const n of nodes || []) {
      if (n?.id) map.set(n.id, n);
      if (n?.children?.length) walk(n.children);
    }
  };
  walk(tree);
  return map;
}

function expandCheckedWithAncestors(fileNodeIds, tree) {
  if (!fileNodeIds?.length) return [];
  const byId = buildIdToNodeMapFromTree(tree);
  const set = new Set(fileNodeIds);
  for (const fid of fileNodeIds) {
    let pId = byId.get(fid)?.parentId;
    while (pId) {
      set.add(pId);
      pId = byId.get(pId)?.parentId ?? null;
    }
  }
  return Array.from(set);
}

function collectFileNodeIdsMatchingDocSet(tree, docIdSet) {
  const out = [];
  const walk = (nodes) => {
    for (const n of nodes || []) {
      if (n?.type === "file" && n?.id) {
        const m = String(n.id).match(/^[^:]+::file::(.+)$/);
        const g = m ? m[1].trim().toLowerCase() : "";
        if (g && docIdSet.has(g)) out.push(n.id);
      }
      if (n?.children?.length) walk(n.children);
    }
  };
  walk(tree);
  return out;
}

/**
 * External (CRO) user study ↔ eISF/ePSF folder tree, scope radios, and DocumentsXML builder.
 * Implemented as a hook so state survives wizard step changes while the modal stays open.
 */
export function useExternalUserAccessScoping({
  organizationId,
  staffId,
  studyOptions = [],
  showAlert,
  studies,
  onStudiesChange,
  /** When true, disable study pickers, scope radios, search, and tree checkboxes */
  readOnly = false,
  /** Comma-separated study ids to highlight + red “External” badge (ExternalStaffISFStudies) */
  highlightedStudyIdsCsv = "",
  /** Comma-separated document ids (optional; if backend omits, use serverDocumentsXml) */
  serverDocumentIdsCsv = "",
  /** DocumentsXML string from profile API — same format as SAVE_UPDATE_USER DocumentsXML */
  serverDocumentsXml = "",
  /**
   * Prefer over XML/CSV when set: array of { StudyId, Category, DocumentIds } from
   * userResponse.data.ExternalStaffISFDocuments (see parseExternalStaffIsfDocumentsStructured).
   */
  serverExternalStaffIsfDocuments = null,
  /** Parent profile fetch still running — keep the assigned table on skeleton. */
  tableBootstrapping = false,
  /** GetProfileDetails ExternalEntityAccess — live grants with SiteId */
  serverEntityAccess = null,
  /** Increment when profile payload is ready so trees + checks hydrate (external profile) */
  hydrationNonce = 0,
}) {
  const [externalStudyTrees, setExternalStudyTrees] = useState({});
  const [externalStudyLoading, setExternalStudyLoading] = useState({});
  const [externalCheckedFolders, setExternalCheckedFolders] = useState({});
  const [expandedKeys, setExpandedKeys] = useState({});
  const [externalStudyScopeByStudyId, setExternalStudyScopeByStudyId] =
    useState({});
  const [externalTreeSearchByKey, setExternalTreeSearchByKey] = useState({});
  const [externalStudyBootstrapLoadingId, setExternalStudyBootstrapLoadingId] =
    useState(null);
  const [studyListQuery, setStudyListQuery] = useState("");
  const [siteListQuery, setSiteListQuery] = useState("");
  const [activeStudyId, setActiveStudyId] = useState(null);
  const [activeSiteId, setActiveSiteId] = useState(null);
  /** When set, Assign study modal is edit-locked to this study only. */
  const [assignLockStudyId, setAssignLockStudyId] = useState(null);
  const [assignSessionActive, setAssignSessionActive] = useState(false);
  /** studyId(lower) → folder probe finished for assign-list filtering */
  const [assignFolderProbeDoneByStudy, setAssignFolderProbeDoneByStudy] =
    useState({});
  const [studySitesByStudyId, setStudySitesByStudyId] = useState({});
  const [studySitesLoadingById, setStudySitesLoadingById] = useState({});
  /** While assign modal probes which sites have folders for the focused study. */
  const [assignSiteFolderProbeBusy, setAssignSiteFolderProbeBusy] =
    useState(false);
  const [binderStudies, setBinderStudies] = useState(null);
  const externalStudyBootstrapCancelledRef = useRef(new Set());
  const studySitesCacheRef = useRef({});
  const studySitesInFlightRef = useRef(new Set());
  const studySitesPromiseRef = useRef({});
  const assignSnapshotRef = useRef(null);
  const studiesRef = useRef(studies);
  studiesRef.current = studies;
  const scopeRef = useRef(externalStudyScopeByStudyId);
  scopeRef.current = externalStudyScopeByStudyId;
  const checkedRef = useRef(externalCheckedFolders);
  checkedRef.current = externalCheckedFolders;
  const treesRef = useRef(externalStudyTrees);
  treesRef.current = externalStudyTrees;
  /** Last hydration nonce that finished successfully (not merely started). */
  const lastHydrationCompletedNonceRef = useRef(-1);
  const lastHydrationNonceRef = useRef(-1);
  const [shareHydrationBusy, setShareHydrationBusy] = useState(false);
  const [orgTimeZoneId, setOrgTimeZoneId] = useState(DEFAULT_ORG_TIME_ZONE_ID);

  useEffect(() => {
    let cancelled = false;
    if (!organizationId) {
      setOrgTimeZoneId(DEFAULT_ORG_TIME_ZONE_ID);
      return undefined;
    }
    (async () => {
      try {
        const response = await fetchOrganizationSetup(
          organizationId,
          staffId,
        );
        if (cancelled) return;
        const { setupRow } =
          parseOrganizationSetupResponse(response) || {};
        const tzId =
          String(setupRow?.TimeZoneId || "").trim() ||
          DEFAULT_ORG_TIME_ZONE_ID;
        setOrgTimeZoneId(tzId);
      } catch {
        if (!cancelled) setOrgTimeZoneId(DEFAULT_ORG_TIME_ZONE_ID);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId, staffId]);

  const highlightedStudyIdSet = useMemo(() => {
    return new Set(parseCommaSeparatedIds(highlightedStudyIdsCsv));
  }, [highlightedStudyIdsCsv]);

  const binderByStudyId = useMemo(() => {
    const map = new Map();
    (binderStudies || []).forEach((row) => {
      const id = String(row?.StudyId ?? row?.studyId ?? "")
        .trim()
        .toLowerCase();
      if (id) map.set(id, row);
    });
    return map;
  }, [binderStudies]);

  const shareableStudyOptions = useMemo(() => {
    return (studyOptions || []).filter((opt) => {
      const id = String(opt?.value ?? "")
        .trim()
        .toLowerCase();
      if (!id) return false;
      const binder = binderByStudyId.get(id);
      return binderHasAny(binder);
    });
  }, [studyOptions, binderByStudyId]);

  const categoriesForStudy = useCallback(
    (studyId) => {
      const binder = binderByStudyId.get(
        String(studyId ?? "")
          .trim()
          .toLowerCase(),
      );
      const cats = [];
      if (binderHasEisf(binder)) cats.push("eISF");
      if (binderHasEpsf(binder)) cats.push("ePSF");
      return cats.length ? cats : ["eISF", "ePSF"];
    },
    [binderByStudyId],
  );

  useEffect(() => {
    if (!organizationId || !staffId) {
      setBinderStudies([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await fetchEisfStudiesForBinder({
          organizationId,
          createdBy: staffId,
        });
        if (!cancelled) setBinderStudies(result?.studies || []);
      } catch (err) {
        console.error("fetchEisfStudiesForBinder", err);
        if (!cancelled) setBinderStudies([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId, staffId]);

  const fetchExternalStudyTree = useCallback(
    async (studyId, templateCategory, siteId) => {
      if (!organizationId || !staffId) return [];
      if (!studyId || !templateCategory || !siteId) return [];

      const cacheKey = folderTreeKey(studyId, siteId, templateCategory);
      setExternalStudyLoading((prev) => ({ ...prev, [cacheKey]: true }));

      try {
        const response = await api.post(API_ENDPOINTS.ISF.GET_STUDY_FOLDERS, {
          OrganizationIdRef: organizationId || "",
          ActionType: "GetAllFolder",
          StaudyIdRef: studyId || "",
          StaffIdRef: staffId || "",
          TemplateCategory: templateCategory,
          SiteIdRef: siteId,
        });

        const data = response?.data || {};
        const restrictedFolders = data?.RestrictedFolders || [];
        const restrictedFolderIds = new Set(
          restrictedFolders.map((rf) =>
            String(rf?.FolderIdRef || "").toLowerCase(),
          ),
        );

        const studyFolders = Array.isArray(data?.StudyFolder)
          ? data.StudyFolder
          : [];
        const seenFolderIds = new Set();
        const uniqueStudyFolders = studyFolders.filter((f) => {
          const id = String(f?.FolderId || "").trim().toLowerCase();
          if (!id || seenFolderIds.has(id)) return false;
          seenFolderIds.add(id);
          return true;
        });

        const folderNodes = uniqueStudyFolders.map((f) => ({
          id: `${templateCategory}::folder::${f.FolderId}`,
          name: f.FolderName,
          type: "folder",
          parentId: (() => {
            const parent = String(f.ParentFolderIdRef || "").trim();
            if (
              !parent ||
              parent === "00000000-0000-0000-0000-000000000000"
            ) {
              return null;
            }
            return `${templateCategory}::folder::${parent}`;
          })(),
          isRestricted: restrictedFolderIds.has(
            String(f.FolderId || "").toLowerCase(),
          ),
        }));

        return buildTree(folderNodes);
      } finally {
        setExternalStudyLoading((prev) => ({ ...prev, [cacheKey]: false }));
      }
    },
    [organizationId, staffId],
  );

  const loadStudySites = useCallback(
    async (studyId) => {
      if (!organizationId || !staffId || !studyId) return [];
      if (Array.isArray(studySitesCacheRef.current[studyId])) {
        return studySitesCacheRef.current[studyId];
      }
      const existingPromise = studySitesPromiseRef.current[studyId];
      if (existingPromise) return existingPromise;

      studySitesInFlightRef.current.add(studyId);
      setStudySitesLoadingById((prev) => ({ ...prev, [studyId]: true }));
      const promise = (async () => {
        try {
          const response = await fetchStudySitesForSetup({
            organizationId,
            studyId,
            templateCategory: "eISF",
            createdBy: staffId,
          });
          const allRows = normalizeStudySiteRows(response);
          const pickerRows = allRows.filter(
            (row) => row.IsConfigured || row.TemplateId,
          );
          const options = buildSiteSelectOptions(
            allRows.length ? allRows : pickerRows,
          ).map((opt) => ({
            ...opt,
            label:
              cleanStudySiteLabel(opt.label) ||
              cleanStudySiteLabel(opt.value) ||
              "Site",
          }));
          studySitesCacheRef.current[studyId] = options;
          setStudySitesByStudyId((prev) => ({ ...prev, [studyId]: options }));
          setExternalStudyScopeByStudyId((prev) => {
            const current = prev?.[studyId] || {};
            const existing = uniqueSiteIds(current.siteIds || []);
            if (existing.length || options.length !== 1) {
              return prev;
            }
            // Only auto-pick the lone site while assigning — never put a
            // removed table row back when sites finish loading.
            if (!assignSnapshotRef.current) {
              return prev;
            }
            return {
              ...prev,
              [studyId]: {
                ...current,
                siteIds: [options[0].value],
              },
            };
          });
          return options;
        } catch (err) {
          console.error("loadStudySites", err);
          studySitesCacheRef.current[studyId] = [];
          setStudySitesByStudyId((prev) => ({ ...prev, [studyId]: [] }));
          return [];
        } finally {
          studySitesInFlightRef.current.delete(studyId);
          delete studySitesPromiseRef.current[studyId];
          setStudySitesLoadingById((prev) => ({ ...prev, [studyId]: false }));
        }
      })();
      studySitesPromiseRef.current[studyId] = promise;
      return promise;
    },
    [organizationId, staffId],
  );

  // Assign modal: probe sites + folder trees for every shareable study so
  // the list only shows studies that actually have site folders.
  useEffect(() => {
    if (!assignSessionActive || assignLockStudyId) {
      setAssignSiteFolderProbeBusy(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setAssignSiteFolderProbeBusy(true);
      try {
        const options = shareableStudyOptions || [];
        for (const opt of options) {
          const studyId = opt?.value;
          if (!studyId || cancelled) continue;
          const idKey = String(studyId).trim().toLowerCase();
          const sites = await loadStudySites(studyId);
          if (cancelled) return;
          const categories = categoriesForStudy(studyId);
          if (!sites?.length || !categories.length) {
            setAssignFolderProbeDoneByStudy((prev) => ({
              ...prev,
              [idKey]: true,
            }));
            continue;
          }
          await Promise.all(
            sites.map(async (site) => {
              const siteId = site?.value;
              if (!siteId || cancelled) return;
              for (const category of categories) {
                if (
                  hasSiteFolderTree(
                    treesRef.current,
                    studyId,
                    siteId,
                    category,
                  )
                ) {
                  continue;
                }
                const tree = await fetchExternalStudyTree(
                  studyId,
                  category,
                  siteId,
                );
                if (cancelled) return;
                setExternalStudyTrees((prev) => {
                  const next = setSiteFolderTree(
                    prev,
                    studyId,
                    siteId,
                    category,
                    tree,
                  );
                  treesRef.current = next;
                  return next;
                });
              }
            }),
          );
          if (cancelled) return;
          setAssignFolderProbeDoneByStudy((prev) => ({
            ...prev,
            [idKey]: true,
          }));
        }
      } finally {
        if (!cancelled) setAssignSiteFolderProbeBusy(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    assignSessionActive,
    assignLockStudyId,
    shareableStudyOptions,
    loadStudySites,
    fetchExternalStudyTree,
    categoriesForStudy,
  ]);

  // After folder probe: drop focus/selection if the study has no site folders.
  useEffect(() => {
    if (!assignSessionActive || assignLockStudyId) return;
    if (!activeStudyId) return;

    const idKey = String(activeStudyId).trim().toLowerCase();
    if (!assignFolderProbeDoneByStudy[idKey]) return;

    const siteOptions = getStudySiteOptions(studySitesByStudyId, activeStudyId);
    const cats = categoriesForStudy(activeStudyId);
    const anyFolders = siteOptions.some((site) =>
      siteHasAnyFolderNodes(
        externalStudyTrees,
        activeStudyId,
        site?.value,
        cats,
      ),
    );
    if (siteOptions.length > 0 && anyFolders) return;

    const opt =
      (shareableStudyOptions || []).find(
        (row) => String(row?.value || "").toLowerCase() === idKey,
      ) || { value: activeStudyId };
    if (isExternalStudySelected(activeStudyId)) {
      handleExternalStudyCheckboxChange(opt, false);
    }
    setActiveStudyId(null);
    setActiveSiteId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    assignSessionActive,
    assignLockStudyId,
    activeStudyId,
    assignFolderProbeDoneByStudy,
    studySitesByStudyId,
    externalStudyTrees,
    shareableStudyOptions,
    categoriesForStudy,
  ]);

  /** Internal rows for DocumentsXML and ExternalStaffISFDocuments API array */
  const buildCroDocumentRowsForSave = useCallback(async () => {
    const studyList = studiesRef.current || [];
    const scopeMap = scopeRef.current || {};
    const checkedMap = checkedRef.current || {};
    const rows = [];
    const toCsv = (arr) => (Array.isArray(arr) ? arr.join(",") : "");

    for (const study of studyList) {
      const studyId = study.StudyId || study.value;
      if (!studyId) continue;

      const accessUntil = normalizeAccessUntilForSave(
        getStudyScope(scopeMap, studyId),
      );
      const siteIds = uniqueSiteIds(
        getStudyScope(scopeMap, studyId)?.siteIds || [],
      );

      siteIds.forEach((siteId) => {
        const studyCats = categoriesForStudy(studyId);
        const catsWithKeys = studyCats.filter((category) =>
          hasFolderCheckKey(checkedMap, studyId, siteId, category),
        );
        // Missing keys used to mean "whole category" — that wrote a
        // removed row straight back. Keep that only when this site has
        // no per-category keys yet (new assign / legacy whole site).
        const catsToWrite = catsWithKeys.length
          ? catsWithKeys
          : studyCats;
        catsToWrite.forEach((category) => {
          const key = folderCheckKey(studyId, siteId, category);
          const hasKey = hasFolderCheckKey(
            checkedMap,
            studyId,
            siteId,
            category,
          );
          const tree = getSiteFolderTree(
            externalStudyTrees,
            studyId,
            siteId,
            category,
          );
          const checks = checkedMap?.[key] || [];
          const folderIds = collectFolderIdsFromCheckedIds(checks);
          const wholeCategory = !hasKey || idsCoverWholeTree(tree, checks);

          if (wholeCategory) {
            rows.push({
              module: "isf",
              studyId,
              siteId,
              category,
              scopeType: "SITE",
              folderids: "",
              accessUntil,
            });
            return;
          }

          if (folderIds.length) {
            rows.push({
              module: "isf",
              studyId,
              siteId,
              category,
              scopeType: "FOLDER",
              folderids: toCsv(folderIds),
              accessUntil,
            });
          }
        });
      });
    }

    return rows;
  }, [externalStudyTrees, fetchExternalStudyTree, categoriesForStudy]);

  const buildCroDocumentListString = useCallback(async () => {
    const internalRows = await buildCroDocumentRowsForSave();
    return documentRowsToDocumentsXml(internalRows);
  }, [buildCroDocumentRowsForSave]);

  /**
   * Same data as DocumentsXML, as objects for SAVE_UPDATE_USER ExternalStaffISFDocuments.
   * One row per study × category (eISF, ePSF) with explicit ScopeType.
   */
  const buildExternalStaffIsfDocumentsPayload = useCallback(async () => {
    const internalRows = await buildCroDocumentRowsForSave();
    return documentRowsToExternalStaffIsfDocuments(internalRows);
  }, [buildCroDocumentRowsForSave]);

  const reset = useCallback(() => {
    setExternalStudyTrees({});
    setExternalStudyLoading({});
    setExternalCheckedFolders({});
    setExpandedKeys({});
    setExternalStudyScopeByStudyId({});
    setExternalTreeSearchByKey({});
    setExternalStudyBootstrapLoadingId(null);
    setStudyListQuery("");
    setSiteListQuery("");
    setActiveStudyId(null);
    setActiveSiteId(null);
    setAssignLockStudyId(null);
    setAssignSessionActive(false);
    setAssignFolderProbeDoneByStudy({});
    setAssignSiteFolderProbeBusy(false);
    setStudySitesByStudyId({});
    setStudySitesLoadingById({});
    studySitesCacheRef.current = {};
    studySitesInFlightRef.current = new Set();
    studySitesPromiseRef.current = {};
    externalStudyBootstrapCancelledRef.current = new Set();
    lastHydrationNonceRef.current = -1;
    lastHydrationCompletedNonceRef.current = -1;
    setShareHydrationBusy(false);
  }, []);

  useEffect(() => {
    (studies || []).forEach((row) => {
      const studyId = row?.StudyId ?? row?.value;
      if (studyId) loadStudySites(studyId);
    });
  }, [studies, loadStudySites]);

  useEffect(() => {
    if (!activeStudyId) return;
    const sites = uniqueSiteIds(
      getStudyScope(externalStudyScopeByStudyId, activeStudyId)?.siteIds || [],
    );
    if (sites.length !== 1) return;
    setActiveSiteId((prev) => prev || sites[0]);
    setExpandedKeys((prev) => ({
      ...prev,
      [siteNodeKey(activeStudyId, sites[0])]: true,
    }));
  }, [activeStudyId, externalStudyScopeByStudyId]);

  useEffect(() => {
    if (!assignSnapshotRef.current) return;
    setExternalCheckedFolders((prev) => {
      let changed = false;
      const next = { ...prev };
      (studies || []).forEach((row) => {
        const studyId = row?.StudyId ?? row?.value;
        if (!studyId) return;
        uniqueSiteIds(
          getStudyScope(externalStudyScopeByStudyId, studyId)?.siteIds || [],
        ).forEach((siteId) => {
          categoriesForStudy(studyId).forEach((category) => {
            const tree = getSiteFolderTree(
              externalStudyTrees,
              studyId,
              siteId,
              category,
            );
            if (!tree.length && !hasSiteFolderTree(
              externalStudyTrees,
              studyId,
              siteId,
              category,
            )) {
              return;
            }
            const key = folderCheckKey(studyId, siteId, category);
            if (Object.prototype.hasOwnProperty.call(next, key)) return;
            next[key] = collectFolderNodeIds(tree);
            changed = true;
          });
        });
      });
      return changed ? next : prev;
    });
  }, [
    studies,
    externalStudyScopeByStudyId,
    externalStudyTrees,
    categoriesForStudy,
  ]);

  useEffect(() => {
    const openStudyIds = Object.entries(expandedKeys)
      .filter(([, on]) => on)
      .map(([key]) =>
        key.startsWith("study:") ? key.slice("study:".length) : null,
      )
      .filter(Boolean);
    const openSiteStudyIds = Object.entries(expandedKeys)
      .filter(([key, on]) => on && key.startsWith("site:"))
      .map(([key]) => key.slice("site:".length).split("::")[0])
      .filter(Boolean);
    const studyIds = [...new Set([...openStudyIds, ...openSiteStudyIds])];
    if (!studyIds.length) return;

    (async () => {
      for (const studyId of studyIds) {
        await loadStudySites(studyId);
      }
      const openSites = Object.entries(expandedKeys)
        .filter(([key, on]) => on && key.startsWith("site:"))
        .map(([key]) => {
          const rest = key.slice("site:".length);
          const splitAt = rest.indexOf("::");
          if (splitAt < 0) return null;
          return {
            studyId: rest.slice(0, splitAt),
            siteId: rest.slice(splitAt + 2),
          };
        })
        .filter(Boolean);

      for (const { studyId, siteId } of openSites) {
        if (!siteId) continue;
        for (const category of categoriesForStudy(studyId)) {
          if (hasSiteFolderTree(externalStudyTrees, studyId, siteId, category)) {
            continue;
          }
          const cacheKey = folderTreeKey(studyId, siteId, category);
          if (externalStudyLoading?.[cacheKey]) continue;
          const tree = await fetchExternalStudyTree(
            studyId,
            category,
            siteId,
          );
          setExternalStudyTrees((prev) =>
            setSiteFolderTree(prev, studyId, siteId, category, tree),
          );
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    expandedKeys,
    externalStudyTrees,
    externalStudyLoading,
    fetchExternalStudyTree,
    loadStudySites,
    categoriesForStudy,
  ]);

  // External profile (or initial server sync): load trees; restore checks from DocumentsXML or CSV ids.
  useEffect(() => {
    if (hydrationNonce < 1) return;
    // Skip only when this nonce already completed — not when a prior run was cancelled mid-flight.
    if (hydrationNonce === lastHydrationCompletedNonceRef.current) return;
    const studyList = studiesRef.current || [];
    if (studyList.length === 0) {
      lastHydrationNonceRef.current = hydrationNonce;
      lastHydrationCompletedNonceRef.current = hydrationNonce;
      setShareHydrationBusy(false);
      return;
    }

    let cancelled = false;
    lastHydrationNonceRef.current = hydrationNonce;
    setShareHydrationBusy(true);
    const grantRows = parseExternalEntityAccessRows(serverEntityAccess);
    const structuredRows =
      parseExternalStaffIsfDocumentsStructured(serverExternalStaffIsfDocuments);
    const xmlParsed = parseCroDocumentListXml(serverDocumentsXml);
    const xmlRows =
      grantRows?.length > 0
        ? grantRows
        : structuredRows?.length > 0
          ? structuredRows
          : xmlParsed;
    const grantByStudy = grantRows?.length
      ? groupCroXmlRowsByStudyId(grantRows)
      : null;
    const xmlByStudy = xmlRows ? groupCroXmlRowsByStudyId(xmlRows) : null;

    (async () => {
      try {
        const treesPayload = {};
        const scopeByStudy = {};
        const checkedMap = {};
        const nextExpanded = {};

        for (const studyEntry of studyList) {
          if (cancelled) return;
          const studyId = studyEntry.StudyId || studyEntry.value;
          if (!studyId) continue;

          nextExpanded[studyNodeKey(studyId)] = true;

          const sidKey = String(studyId).trim().toLowerCase();
          const plan = xmlByStudy?.get(sidKey);
          const grantPlan = grantByStudy?.get(sidKey);
          const siteIds = uniqueSiteIds([
            ...(grantPlan?.siteIds || []),
            ...(plan?.siteIds || []),
          ]);
          if (!treesPayload[studyId]) treesPayload[studyId] = {};

          const loadSiteTrees = async (siteId) => {
            const siteTrees = {};
            for (const category of categoriesForStudy(studyId)) {
              siteTrees[category] = await fetchExternalStudyTree(
                studyId,
                category,
                siteId,
              );
            }
            treesPayload[studyId][siteId] = {
              ...(treesPayload[studyId][siteId] || {}),
              ...siteTrees,
            };
            return siteTrees;
          };

          const applySiteChecks = (siteId, sitePlan, siteTrees) => {
            const granted = [];
            if (sitePlan?.eISF) granted.push("eISF");
            if (sitePlan?.ePSF) granted.push("ePSF");
            if (
              !granted.length &&
              !Object.keys(plan.bySite || {}).length
            ) {
              if (plan?.eISF) granted.push("eISF");
              if (plan?.ePSF) granted.push("ePSF");
            }
            granted.forEach((category) => {
              const planRow = sitePlan?.[category] || plan?.[category];
              checkedMap[folderCheckKey(studyId, siteId, category)] =
                checkedIdsFromPlanRow(planRow, siteTrees?.[category] || []);
            });
          };

          if (plan) {
            const untilParts = splitAccessUntilForUi(
              plan.eISF?.accessUntil ||
                plan.ePSF?.accessUntil ||
                Object.values(plan.bySite || {})[0]?.eISF?.accessUntil ||
                Object.values(plan.bySite || {})[0]?.ePSF?.accessUntil ||
                grantPlan?.eISF?.accessUntil ||
                grantPlan?.ePSF?.accessUntil ||
                "",
            );
            scopeByStudy[studyId] = {
              ...untilParts,
              siteIds,
            };
            await loadStudySites(studyId);
            for (const siteId of siteIds) {
              if (cancelled) return;
              const siteTrees = await loadSiteTrees(siteId);
              const sitePlan =
                plan.bySite?.[String(siteId).trim().toLowerCase()] || {};
              applySiteChecks(siteId, sitePlan, siteTrees);
            }
          } else {
            scopeByStudy[studyId] = { siteIds: [] };
            await loadStudySites(studyId);
          }
        }

        if (cancelled) return;
        const firstId = studyList[0]?.StudyId || studyList[0]?.value || null;
        const firstSites = firstId
          ? uniqueSiteIds(scopeByStudy?.[firstId]?.siteIds || [])
          : [];
        if (firstId && firstSites[0]) {
          nextExpanded[siteNodeKey(firstId, firstSites[0])] = true;
        }
        setExternalStudyTrees((prev) => ({ ...prev, ...treesPayload }));
        setExternalStudyScopeByStudyId((prev) => ({ ...prev, ...scopeByStudy }));
        setExternalCheckedFolders((prev) => ({ ...prev, ...checkedMap }));
        setExpandedKeys((prev) => ({ ...prev, ...nextExpanded }));
        if (firstId) {
          setActiveStudyId(firstId);
          setActiveSiteId(firstSites[0] || null);
        }
        lastHydrationCompletedNonceRef.current = hydrationNonce;
      } catch (error) {
        console.error("external share hydration", error);
        // Mark complete so the table is not stuck on skeleton after a failure.
        if (!cancelled) {
          lastHydrationCompletedNonceRef.current = hydrationNonce;
        }
      } finally {
        if (!cancelled) setShareHydrationBusy(false);
      }
    })();

    return () => {
      cancelled = true;
      // Prior run was interrupted — clear busy so a same-nonce re-entry can finish
      // (or the table can recover instead of skeleton forever).
      setShareHydrationBusy(false);
    };
  }, [
    hydrationNonce,
    serverDocumentIdsCsv,
    serverDocumentsXml,
    serverExternalStaffIsfDocuments,
    serverEntityAccess,
    fetchExternalStudyTree,
    categoriesForStudy,
    loadStudySites,
  ]);

  useEffect(() => {
    (studies || []).forEach((row) => {
      const studyId = row?.StudyId || row?.value;
      if (studyId) loadStudySites(studyId);
    });
  }, [studies, loadStudySites]);

  const isExternalStudySelected = useCallback(
    (studyId) =>
      (studies || []).some(
        (s) =>
          String(s?.StudyId ?? s?.value ?? "").toLowerCase() ===
          String(studyId ?? "").toLowerCase(),
      ),
    [studies],
  );

  const handleExternalStudyCheckboxChange = async (studyOption, checked) => {
    if (readOnly) return;
    const studyId = studyOption?.value;
    const label = studyOption?.label ?? "";
    if (!studyId) return;

    if (!checked) {
      if (externalStudyBootstrapLoadingId === studyId) {
        externalStudyBootstrapCancelledRef.current.add(studyId);
        setExternalStudyBootstrapLoadingId(null);
        return;
      }
      onStudiesChange((prev) =>
        (prev || []).filter(
          (s) =>
            String(s?.StudyId ?? "").toLowerCase() !==
            String(studyId).toLowerCase(),
        ),
      );
      setExpandedKeys((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((key) => {
          if (
            key === studyNodeKey(studyId) ||
            key.startsWith(`site:${studyId}::`) ||
            key.startsWith(`cat:${studyId}::`)
          ) {
            delete next[key];
          }
        });
        return next;
      });
      setExternalStudyScopeByStudyId((prev) => {
        const next = { ...prev };
        delete next[studyId];
        return next;
      });
      setExternalCheckedFolders((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((key) => {
          if (key.startsWith(`${studyId}::`)) delete next[key];
        });
        return next;
      });
      if (String(activeStudyId || "") === String(studyId)) {
        setActiveStudyId(null);
        setActiveSiteId(null);
      }
      return;
    }

    if (externalStudyBootstrapLoadingId) return;
    if (isExternalStudySelected(studyId)) return;

    externalStudyBootstrapCancelledRef.current.delete(studyId);
    setExternalStudyBootstrapLoadingId(studyId);
    try {
      if (externalStudyBootstrapCancelledRef.current.has(studyId)) {
        externalStudyBootstrapCancelledRef.current.delete(studyId);
        return;
      }
      onStudiesChange((prev) => {
        const current = prev || [];
        if (
          current.some(
            (s) =>
              String(s?.StudyId ?? "").toLowerCase() ===
              String(studyId).toLowerCase(),
          )
        ) {
          return current;
        }
        return [
          ...current,
          {
            StudyId: studyId,
            label,
            studyNumber: studyOption?.studyNumber || "",
            studyName: studyOption?.studyName || "",
            StudyNumber: studyOption?.studyNumber || "",
            StudyName: studyOption?.studyName || "",
            studyColor:
              studyOption?.studyColor || studyOption?.StudySchColor || "",
            StudySchColor:
              studyOption?.StudySchColor || studyOption?.studyColor || "",
          },
        ];
      });
      setExternalStudyScopeByStudyId((prev) => ({
        ...prev,
        [studyId]: {
          ...(prev?.[studyId] || {}),
          siteIds: uniqueSiteIds(prev?.[studyId]?.siteIds || []),
        },
      }));
      setExpandedKeys((prev) => ({ ...prev, [studyNodeKey(studyId)]: true }));
      setActiveStudyId(studyId);
      setActiveSiteId(null);
      await loadStudySites(studyId);
    } catch (err) {
      console.error("External study bootstrap failed:", err);
      if (!externalStudyBootstrapCancelledRef.current.has(studyId)) {
        showAlert({
          title: "Error",
          message:
            err?.response?.data?.message ||
            err?.message ||
            "Could not load study sites.",
          type: "danger",
        });
      }
    } finally {
      setExternalStudyBootstrapLoadingId(null);
      externalStudyBootstrapCancelledRef.current.delete(studyId);
    }
  };

  const getSnapshotStudyIds = useCallback(() => {
    const snap = assignSnapshotRef.current;
    return new Set(
      (snap?.studies || [])
        .map((row) =>
          String(row?.StudyId ?? row?.value ?? "")
            .trim()
            .toLowerCase(),
        )
        .filter(Boolean),
    );
  }, []);

  const getSnapshotSiteIds = useCallback((studyId) => {
    const snap = assignSnapshotRef.current;
    return new Set(
      uniqueSiteIds(
        getStudyScope(snap?.scope, studyId)?.siteIds || [],
      ).map((id) => String(id).toLowerCase()),
    );
  }, []);

  // Drop newly picked sites that have no folders (e.g. lone-site auto-pick).
  useEffect(() => {
    if (!assignSnapshotRef.current || assignLockStudyId) return;
    if (!activeStudyId || assignSiteFolderProbeBusy) return;
    const cats = categoriesForStudy(activeStudyId);
    const selected = uniqueSiteIds(
      getStudyScope(externalStudyScopeByStudyId, activeStudyId)?.siteIds || [],
    );
    if (!selected.length) return;

    const snapshotTaken = getSnapshotSiteIds(activeStudyId);
    const kept = selected.filter((siteId) => {
      if (
        !siteFolderTreesReady(
          externalStudyTrees,
          activeStudyId,
          siteId,
          cats,
        )
      ) {
        return true;
      }
      if (
        siteHasAnyFolderNodes(
          externalStudyTrees,
          activeStudyId,
          siteId,
          cats,
        )
      ) {
        return true;
      }
      return snapshotTaken.has(String(siteId).toLowerCase());
    });

    if (kept.length === selected.length) return;

    setExternalStudyScopeByStudyId((prev) => ({
      ...prev,
      [activeStudyId]: {
        ...(prev?.[activeStudyId] || {}),
        siteIds: kept,
      },
    }));
    setExternalCheckedFolders((prev) => {
      const next = { ...prev };
      selected.forEach((siteId) => {
        if (
          kept.some(
            (id) =>
              String(id).toLowerCase() === String(siteId).toLowerCase(),
          )
        ) {
          return;
        }
        cats.forEach((category) => {
          delete next[folderCheckKey(activeStudyId, siteId, category)];
        });
      });
      return next;
    });
    if (
      activeSiteId &&
      !kept.some(
        (id) =>
          String(id).toLowerCase() === String(activeSiteId).toLowerCase(),
      )
    ) {
      setActiveSiteId(kept[0] || null);
    }
  }, [
    activeStudyId,
    activeSiteId,
    assignLockStudyId,
    assignSiteFolderProbeBusy,
    externalStudyScopeByStudyId,
    externalStudyTrees,
    categoriesForStudy,
    getSnapshotSiteIds,
  ]);

  const getStudiesMissingSites = useCallback(() => {
    return (studies || []).filter((row) => {
      const studyId = row?.StudyId ?? row?.value;
      if (!studyId) return false;
      return uniqueSiteIds(
        getStudyScope(externalStudyScopeByStudyId, studyId)?.siteIds || [],
      ).length === 0;
    });
  }, [studies, externalStudyScopeByStudyId]);

  const getStudyShareValidationError = useCallback(() => {
    const lockId = String(assignLockStudyId || "").trim();
    const snap = assignSnapshotRef.current;
    const snapshotIds = getSnapshotStudyIds();
    const sameId = (left, right) =>
      String(left || "").toLowerCase() === String(right || "").toLowerCase();

    const rows = (studies || []).filter((row) => {
      const studyId = row?.StudyId ?? row?.value;
      if (!studyId) return false;
      if (lockId) return sameId(studyId, lockId);
      if (snap) return !snapshotIds.has(String(studyId).toLowerCase());
      return true;
    });

    const addedExistingSite = Boolean(
      snap &&
        !lockId &&
        (studies || []).some((row) => {
          const studyId = row?.StudyId ?? row?.value;
          if (!studyId || !snapshotIds.has(String(studyId).toLowerCase())) {
            return false;
          }
          const before = getSnapshotSiteIds(studyId);
          return uniqueSiteIds(
            getStudyScope(externalStudyScopeByStudyId, studyId)?.siteIds || [],
          ).some((id) => !before.has(String(id).toLowerCase()));
        }),
    );

    if (!lockId && !snap) {
      return "";
    }
    if (lockId) {
      if (!rows.length) return "Please select a study.";
    } else if (rows.length === 0 && !addedExistingSite) {
      return "Please select a study.";
    }

    const missing = rows.filter((row) => {
      const studyId = row?.StudyId ?? row?.value;
      return (
        uniqueSiteIds(
          getStudyScope(externalStudyScopeByStudyId, studyId)?.siteIds || [],
        ).length === 0
      );
    });
    if (missing.length > 0) {
      const names = missing
        .map((row) =>
          String(row?.label || row?.StudyName || row?.StudyId || "").trim(),
        )
        .filter(Boolean)
        .join(", ");
      return names
        ? `Select a site for ${names}.`
        : "Select a site for the study you are assigning.";
    }
    return "";
  }, [
    assignLockStudyId,
    studies,
    externalStudyScopeByStudyId,
    getSnapshotStudyIds,
    getSnapshotSiteIds,
  ]);

  /**
   * Rows that are new in the current assign session only:
   * - studies not present in assignSnapshot, and/or
   * - newly added sites on studies that were already in the snapshot.
   * Used for multi-user add-mode merge saves (never copy one user's full XML).
   */
  const buildAssignDeltaDocumentRows = useCallback(async () => {
    const allRows = await buildCroDocumentRowsForSave();
    const snap = assignSnapshotRef.current;
    const lockId = String(assignLockStudyId || "").trim();
    if (!snap && !lockId) return allRows;

    const snapshotIds = getSnapshotStudyIds();
    return allRows.filter((row) => {
      const studyId = row?.studyId;
      if (!studyId) return false;
      if (lockId) {
        return (
          String(studyId).toLowerCase() === String(lockId).toLowerCase()
        );
      }
      if (!snapshotIds.has(String(studyId).toLowerCase())) return true;
      const before = getSnapshotSiteIds(studyId);
      return !before.has(String(row.siteId || "").toLowerCase());
    });
  }, [
    assignLockStudyId,
    buildCroDocumentRowsForSave,
    getSnapshotStudyIds,
    getSnapshotSiteIds,
  ]);

  const toggleStudySite = (studyId, siteId, checked) => {
    if (readOnly || !studyId || !siteId) return;
    setExternalStudyScopeByStudyId((prev) => {
      const current = prev?.[studyId] || {};
      const existing = uniqueSiteIds(current.siteIds || []);
      const key = String(siteId).toLowerCase();
      const nextIds = checked
        ? uniqueSiteIds([...existing, siteId])
        : existing.filter((id) => String(id).toLowerCase() !== key);
      return {
        ...prev,
        [studyId]: {
          ...current,
          siteIds: nextIds,
        },
      };
    });
    if (!checked) {
      setExternalCheckedFolders((prev) => {
        const next = { ...prev };
        delete next[folderCheckKey(studyId, siteId, "eISF")];
        delete next[folderCheckKey(studyId, siteId, "ePSF")];
        return next;
      });
      return;
    }
    setActiveStudyId(studyId);
    setActiveSiteId(checked ? siteId : (prev) => (prev === siteId ? null : prev));
    setExpandedKeys((prev) => ({
      ...prev,
      [studyNodeKey(studyId)]: true,
      [siteNodeKey(studyId, siteId)]: checked,
    }));
    const trees = externalStudyTrees?.[studyId]?.[siteId] || {};
    setExternalCheckedFolders((prev) => {
      const next = { ...prev };
      categoriesForStudy(studyId).forEach((category) => {
        const key = folderCheckKey(studyId, siteId, category);
        if (Object.prototype.hasOwnProperty.call(next, key)) return;
        const tree = trees[category];
        if (tree) next[key] = collectFolderNodeIds(tree);
      });
      return next;
    });
  };

  const focusStudy = (studyId) => {
    setActiveStudyId(studyId || null);
    setActiveSiteId(null);
    setSiteListQuery("");
    if (studyId) {
      setExpandedKeys((prev) => ({ ...prev, [studyNodeKey(studyId)]: true }));
      loadStudySites(studyId);
    }
  };

  const focusSite = (studyId, siteId) => {
    setActiveStudyId(studyId || null);
    setActiveSiteId(siteId || null);
    if (studyId && siteId) {
      setExpandedKeys((prev) => ({
        ...prev,
        [studyNodeKey(studyId)]: true,
        [siteNodeKey(studyId, siteId)]: true,
      }));
    }
  };

  const selectStudyRow = async (opt) => {
    if (!opt?.value) return;
    const studyId = opt.value;
    if (assignLockStudyId) {
      if (
        String(studyId).toLowerCase() !==
        String(assignLockStudyId).toLowerCase()
      ) {
        return;
      }
      return;
    }
    focusStudy(studyId);
    if (readOnly) return;
    if (!isExternalStudySelected(studyId)) {
      await handleExternalStudyCheckboxChange(opt, true);
    }
  };

  const unshareStudyRow = (e, opt) => {
    e.stopPropagation();
    if (readOnly || assignLockStudyId || !opt?.value) return;
    handleExternalStudyCheckboxChange(opt, false);
  };

  const selectSiteRow = (studyId, siteId) => {
    if (!studyId || !siteId) return;
    const cats = categoriesForStudy(studyId);
    if (
      siteFolderTreesReady(externalStudyTrees, studyId, siteId, cats) &&
      !siteHasAnyFolderNodes(externalStudyTrees, studyId, siteId, cats) &&
      !assignLockStudyId
    ) {
      return;
    }
    if (readOnly) {
      focusSite(studyId, siteId);
      return;
    }
    if (!assignLockStudyId) {
      toggleStudySite(studyId, siteId, true);
      focusSite(studyId, siteId);
      return;
    }
    uniqueSiteIds(
      getStudyScope(externalStudyScopeByStudyId, studyId)?.siteIds || [],
    ).forEach((oldId) => {
      if (String(oldId).toLowerCase() === String(siteId).toLowerCase()) return;
      toggleStudySite(studyId, oldId, false);
    });
    toggleStudySite(studyId, siteId, true);
    focusSite(studyId, siteId);
  };

  const getCategoryCheckState = (studyId, siteId, category) => {
    const key = folderCheckKey(studyId, siteId, category);
    const hasKey = Object.prototype.hasOwnProperty.call(
      externalCheckedFolders || {},
      key,
    );
    const tree = getSiteFolderTree(
      externalStudyTrees,
      studyId,
      siteId,
      category,
    );
    const checks = externalCheckedFolders?.[key] || [];
    if (!hasKey) return { checked: true, indeterminate: false };
    const all = collectFolderNodeIds(tree);
    if (!all.length) {
      return { checked: checks.length > 0, indeterminate: false };
    }
    const set = new Set(checks);
    const selected = all.filter((id) => set.has(id)).length;
    if (selected === 0) return { checked: false, indeterminate: false };
    if (selected === all.length) return { checked: true, indeterminate: false };
    return { checked: true, indeterminate: true };
  };

  const renderStudiesStep = () => {
    const query = String(studyListQuery || "").trim().toLowerCase();
    const sameStudyId = (left, right) =>
      String(left || "").toLowerCase() === String(right || "").toLowerCase();
    const snapshotStudyIds = getSnapshotStudyIds();
    const studyHasAssignableSite = (studyId) => {
      const idKey = String(studyId || "")
        .trim()
        .toLowerCase();
      if (!idKey) return false;
      const taken = getSnapshotSiteIds(studyId);
      const siteOptions = getStudySiteOptions(studySitesByStudyId, studyId);
      const sitesLoaded = Object.keys(studySitesByStudyId || {}).some(
        (key) => String(key).toLowerCase() === idKey,
      );
      // Hide until sites + folder probe have finished for this study.
      if (!sitesLoaded || studySitesLoadingById?.[studyId]) {
        return false;
      }
      if (!assignFolderProbeDoneByStudy[idKey]) {
        return false;
      }
      if (!siteOptions.length) return false;

      const freeSites = siteOptions.filter(
        (site) => !taken.has(String(site?.value || "").toLowerCase()),
      );
      if (!freeSites.length) return false;

      const cats = categoriesForStudy(studyId);
      return freeSites.some((site) =>
        siteHasAnyFolderNodes(
          externalStudyTrees,
          studyId,
          site?.value,
          cats,
        ),
      );
    };
    const sourceStudyOptions = assignLockStudyId
      ? (shareableStudyOptions || []).filter((opt) =>
          sameStudyId(opt?.value, assignLockStudyId),
        )
      : (shareableStudyOptions || []).filter((opt) =>
          studyHasAssignableSite(opt?.value),
        );
    const lockedFallback =
      assignLockStudyId && sourceStudyOptions.length === 0
        ? [
            {
              value: assignLockStudyId,
              label:
                (studies || []).find((row) =>
                  sameStudyId(row?.StudyId ?? row?.value, assignLockStudyId),
                )?.label ||
                (studies || []).find((row) =>
                  sameStudyId(row?.StudyId ?? row?.value, assignLockStudyId),
                )?.StudyName ||
                String(assignLockStudyId),
            },
          ]
        : sourceStudyOptions;
    const filteredStudyOptions = assignLockStudyId
      ? lockedFallback
      : lockedFallback.filter((opt) =>
          String(opt?.label || "")
            .toLowerCase()
            .includes(query),
        );
    const focusedStudyId = assignLockStudyId || activeStudyId;
    const focusedStudy = (shareableStudyOptions || [])
      .concat(lockedFallback)
      .find((opt) => sameStudyId(opt?.value, focusedStudyId));
    const focusedStudySelected = focusedStudyId
      ? isExternalStudySelected(focusedStudyId)
      : false;
    const selectedSiteIds = uniqueSiteIds(
      getStudyScope(externalStudyScopeByStudyId, focusedStudyId)?.siteIds || [],
    );
    const studySiteOptions = getStudySiteOptions(
      studySitesByStudyId,
      focusedStudyId,
    );
    const takenSiteIds = assignLockStudyId
      ? new Set()
      : getSnapshotSiteIds(focusedStudyId);
    const studyCats = focusedStudyId
      ? categoriesForStudy(focusedStudyId)
      : [];
    const selectedSiteIdSet = new Set(
      selectedSiteIds.map((id) => String(id).toLowerCase()),
    );
    const assignableSiteOptions = studySiteOptions.filter((site) => {
      const siteId = site?.value;
      if (!siteId) return false;
      if (takenSiteIds.has(String(siteId).toLowerCase())) return false;
      // Keep an already-chosen site visible (edit / in-progress assign).
      if (selectedSiteIdSet.has(String(siteId).toLowerCase())) return true;
      if (
        !siteFolderTreesReady(
          externalStudyTrees,
          focusedStudyId,
          siteId,
          studyCats,
        )
      ) {
        return false;
      }
      return siteHasAnyFolderNodes(
        externalStudyTrees,
        focusedStudyId,
        siteId,
        studyCats,
      );
    });
    const sitesWithFoldersCount = studySiteOptions.filter((site) => {
      const siteId = site?.value;
      if (!siteId) return false;
      if (selectedSiteIdSet.has(String(siteId).toLowerCase())) return true;
      return (
        siteFolderTreesReady(
          externalStudyTrees,
          focusedStudyId,
          siteId,
          studyCats,
        ) &&
        siteHasAnyFolderNodes(
          externalStudyTrees,
          focusedStudyId,
          siteId,
          studyCats,
        )
      );
    }).length;
    const siteQuery = String(siteListQuery || "").trim().toLowerCase();
    const filteredSiteOptions = assignableSiteOptions.filter((site) =>
      String(site?.label || "")
        .toLowerCase()
        .includes(siteQuery),
    );
    const sitesLoading =
      Boolean(studySitesLoadingById?.[focusedStudyId]) ||
      Boolean(focusedStudyId && assignSiteFolderProbeBusy);
    const focusedSiteId = activeSiteId;
    const focusedSite = studySiteOptions.find(
      (site) =>
        String(site?.value || "").toLowerCase() ===
        String(focusedSiteId || "").toLowerCase(),
    );
    const focusedSiteChecked = selectedSiteIds.some(
      (id) =>
        String(id).toLowerCase() === String(focusedSiteId || "").toLowerCase(),
    );
    const missingSites = focusedStudySelected && selectedSiteIds.length === 0;

    const selectionSummary = (() => {
      const items = [];
      const sameId = (left, right) =>
        String(left || "").toLowerCase() === String(right || "").toLowerCase();
      (studies || []).forEach((study) => {
        const studyId = study?.StudyId ?? study?.value;
        if (!studyId) return;
        if (assignLockStudyId && !sameId(studyId, assignLockStudyId)) return;

        const idKey = String(studyId).trim().toLowerCase();
        const siteIds = uniqueSiteIds(
          getStudyScope(externalStudyScopeByStudyId, studyId)?.siteIds || [],
        );
        if (!siteIds.length) return;

        const isNewStudy = !snapshotStudyIds.has(idKey);
        const snapshotSites = getSnapshotSiteIds(studyId);
        const changedSites = siteIds.filter(
          (siteId) => !snapshotSites.has(String(siteId).toLowerCase()),
        );
        // New assign: only show new/changed picks. Edit lock: show current study sites.
        if (!assignLockStudyId && !isNewStudy && !changedSites.length) {
          return;
        }
        const sitesToList = assignLockStudyId || isNewStudy ? siteIds : changedSites;
        const studyLabel =
          study?.label ||
          study?.StudyName ||
          (shareableStudyOptions || []).find((opt) => sameId(opt?.value, studyId))
            ?.label ||
          String(studyId);
        const untilRaw = normalizeAccessUntilForSave(
          getStudyScope(externalStudyScopeByStudyId, studyId),
        );
        const untilLabel = untilRaw
          ? formatOrgWallClockDisplay(untilRaw) || untilRaw
          : "Unlimited";
        const studyCats = categoriesForStudy(studyId);
        const siteBlocks = sitesToList.map((siteId) => {
          const siteName =
            resolveAssignedSiteName(siteId, studySitesByStudyId, studyId) ||
            getStudySiteOptions(studySitesByStudyId, studyId).find((s) =>
              sameId(s?.value, siteId),
            )?.label ||
            "Site";
          const cats = studyCats
            .map((category) => {
              const key = folderCheckKey(studyId, siteId, category);
              const tree = getSiteFolderTree(
                externalStudyTrees,
                studyId,
                siteId,
                category,
              );
              const total = collectFolderNodeIds(tree).length;
              if (!total && !hasFolderCheckKey(externalCheckedFolders || {}, studyId, siteId, category)) {
                // Still include category if tree not loaded but site selected.
                if (!hasSiteFolderTree(externalStudyTrees, studyId, siteId, category)) {
                  return { category, folders: "…", whole: false };
                }
              }
              const hasKey = Object.prototype.hasOwnProperty.call(
                externalCheckedFolders || {},
                key,
              );
              const checks = externalCheckedFolders?.[key] || [];
              const folderIds = collectFolderIdsFromCheckedIds(checks);
              const whole = !hasKey || idsCoverWholeTree(tree, checks);
              const assigned = whole ? total : folderIds.length;
              if (!total && !hasKey) {
                return { category, folders: "—", whole: false };
              }
              return {
                category,
                folders: total ? `${assigned} / ${total}` : "—",
                whole,
              };
            })
            .filter(Boolean);
          return { siteId, siteName, cats };
        });
        items.push({
          studyId,
          studyLabel,
          untilLabel,
          siteBlocks,
        });
      });
      return items;
    })();

    return (
      <div className="external-studies-tree external-studies-assign">
        <div className="external-grant-cols">
          <div className="external-grant-col">
            <div className="external-grant-col__head">
              <span>Studies</span>
              <span className="external-studies-assign__count">
                {assignLockStudyId
                  ? "Editing"
                  : assignSiteFolderProbeBusy && sourceStudyOptions.length === 0
                    ? "Checking…"
                    : `${sourceStudyOptions.length} available`}
              </span>
            </div>
            {assignLockStudyId ? null : (
            <div className="external-grant-col__search">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search studies"
                value={studyListQuery}
                onChange={(e) => setStudyListQuery(e.target.value)}
                aria-label="Search studies"
              />
            </div>
            )}
            <div className="external-grant-col__body">
              {binderStudies == null && !assignLockStudyId ? (
                <div className="text-muted small">Loading studies…</div>
              ) : (studyOptions || []).length === 0 && !assignLockStudyId ? (
                <div className="text-muted small">No studies available.</div>
              ) : shareableStudyOptions.length === 0 && !assignLockStudyId ? (
                <div className="text-muted small">
                  No studies have an eISF or ePSF yet.
                </div>
              ) : assignSiteFolderProbeBusy &&
                sourceStudyOptions.length === 0 &&
                !assignLockStudyId ? (
                <div className="text-muted small">
                  Checking which studies have sites and folders…
                </div>
              ) : sourceStudyOptions.length === 0 && !assignLockStudyId ? (
                <div className="text-muted small">
                  All studies are fully assigned.
                </div>
              ) : filteredStudyOptions.length === 0 ? (
                <div className="text-muted small">
                  {assignLockStudyId
                    ? "This study is no longer available."
                    : "No studies match your search."}
                </div>
              ) : (
                filteredStudyOptions.map((opt) => {
                  const studyId = opt.value;
                  const sel = isExternalStudySelected(studyId);
                  const loadingThis =
                    externalStudyBootstrapLoadingId === studyId;
                  const isActive =
                    String(focusedStudyId || "").toLowerCase() ===
                    String(studyId).toLowerCase();
                  const alreadyShared = snapshotStudyIds.has(
                    String(studyId).toLowerCase(),
                  );
                  return (
                    <button
                      key={String(studyId)}
                      type="button"
                      className={`external-grant-item${
                        sel ? " is-assigned" : ""
                      }${isActive ? " is-active" : ""}`}
                      disabled={Boolean(
                        !readOnly &&
                          externalStudyBootstrapLoadingId &&
                          !loadingThis,
                      )}
                      onClick={() => selectStudyRow(opt)}
                    >
                      <span className="external-grant-item__label" title={opt.label}>
                        {opt.label}
                      </span>
                      {sel &&
                      !alreadyShared &&
                      !readOnly &&
                      !assignLockStudyId ? (
                        <span
                          role="button"
                          tabIndex={0}
                          className="external-grant-item__remove"
                          title="Remove selection"
                          aria-label="Remove selection"
                          onClick={(e) => unshareStudyRow(e, opt)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              unshareStudyRow(e, opt);
                            }
                          }}
                        >
                          <i className="fas fa-trash-alt" aria-hidden="true" />
                        </span>
                      ) : null}
                      {loadingThis ? (
                        <span
                          className="spinner-border spinner-border-sm text-primary"
                          role="status"
                          aria-hidden
                        />
                      ) : null}
                    </button>
                  );
                })
              )}
            </div>
            {focusedStudySelected ? (
              <VisibleUntilFields
                dateIso={
                  getStudyScope(externalStudyScopeByStudyId, focusedStudyId)
                    ?.accessUntilDate || ""
                }
                timeHHmm={
                  getStudyScope(externalStudyScopeByStudyId, focusedStudyId)
                    ?.accessUntilTime || ""
                }
                disabled={readOnly}
                onChange={(next) => {
                  if (readOnly) return;
                  setExternalStudyScopeByStudyId((prev) => ({
                    ...prev,
                    [focusedStudyId]: {
                      ...(prev?.[focusedStudyId] || {}),
                      ...next,
                    },
                  }));
                }}
              />
            ) : null}
          </div>

          <div className="external-grant-col">
            <div className="external-grant-col__head">
              <span>Sites</span>
              {focusedStudy ? (
                <span className="external-grant-col__sub">
                  {focusedStudy.label}
                </span>
              ) : null}
            </div>
            <div className="external-grant-col__search">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search sites"
                value={siteListQuery}
                disabled={!focusedStudyId}
                onChange={(e) => setSiteListQuery(e.target.value)}
                aria-label="Search sites"
              />
            </div>
            <div className="external-grant-col__body">
              {!focusedStudyId ? (
                <div className="text-muted small">
                  Select a study to see its sites.
                </div>
              ) : sitesLoading ? (
                <div className="text-muted small">Loading sites…</div>
              ) : studySiteOptions.length === 0 ? (
                <div className="text-muted small">
                  No sites are set up for this study yet.
                </div>
              ) : sitesWithFoldersCount === 0 && !assignLockStudyId ? (
                <div className="text-muted small">
                  No sites with folders for this study.
                </div>
              ) : assignableSiteOptions.length === 0 && !assignLockStudyId ? (
                <div className="text-muted small">
                  All sites are already assigned.
                </div>
              ) : filteredSiteOptions.length === 0 ? (
                <div className="text-muted small">
                  No sites match your search.
                </div>
              ) : (
                filteredSiteOptions.map((siteOpt) => {
                  const siteId = siteOpt.value;
                  const siteChecked = selectedSiteIds.some(
                    (id) =>
                      String(id).toLowerCase() ===
                      String(siteId).toLowerCase(),
                  );
                  const isActive =
                    String(focusedSiteId || "").toLowerCase() ===
                    String(siteId).toLowerCase();
                  return (
                    <button
                      key={siteId}
                      type="button"
                      className={`external-grant-item${
                        siteChecked ? " is-assigned" : ""
                      }${isActive ? " is-active" : ""}`}
                      disabled={!readOnly && !focusedStudySelected}
                      onClick={() =>
                        selectSiteRow(focusedStudyId, siteId)
                      }
                    >
                      <span
                        className="external-grant-item__label"
                        title={siteOpt.label}
                      >
                        {siteOpt.label}
                      </span>
                    </button>
                  );
                })
              )}
              {missingSites && !sitesLoading ? (
                <div className="external-studies-assign-row__site-error">
                  Select a site.
                </div>
              ) : null}
            </div>
          </div>

          <div className="external-grant-col external-grant-col--folders">
            <div className="external-grant-col__head">
              <span>Folders</span>
              {focusedSite ? (
                <span className="external-grant-col__sub">
                  {focusedSite.label}
                </span>
              ) : null}
            </div>
            <div className="external-grant-col__search">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search folders"
                value={
                  focusedStudyId && focusedSiteId
                    ? externalTreeSearchByKey?.[
                        `col:${focusedStudyId}::${focusedSiteId}`
                      ] || ""
                    : ""
                }
                disabled={readOnly || !focusedStudyId || !focusedSiteId}
                onChange={(e) => {
                  if (!focusedStudyId || !focusedSiteId) return;
                  const searchKey = `col:${focusedStudyId}::${focusedSiteId}`;
                  setExternalTreeSearchByKey((prev) => ({
                    ...prev,
                    [searchKey]: e.target.value,
                  }));
                }}
                aria-label="Search folders"
              />
            </div>
            <div className="external-grant-col__body external-grant-col__body--folders">
              {!focusedStudyId || !focusedSiteId ? (
                <div className="text-muted small">
                  Select a site to choose eISF and ePSF folders.
                </div>
              ) : !focusedSiteChecked ? (
                <div className="text-muted small">
                  Select a site to choose folders.
                </div>
              ) : (
                categoriesForStudy(focusedStudyId).map((category) => {
                  const cacheKey = folderTreeKey(
                    focusedStudyId,
                    focusedSiteId,
                    category,
                  );
                  const hasTreeKey = hasSiteFolderTree(
                    externalStudyTrees,
                    focusedStudyId,
                    focusedSiteId,
                    category,
                  );
                  const fullTree = getSiteFolderTree(
                    externalStudyTrees,
                    focusedStudyId,
                    focusedSiteId,
                    category,
                  );
                  const searchKey = folderCheckKey(
                    focusedStudyId,
                    focusedSiteId,
                    category,
                  );
                  const searchTerm =
                    externalTreeSearchByKey?.[
                      `col:${focusedStudyId}::${focusedSiteId}`
                    ] || "";
                  const filteredTree = filterTreeByQuery(fullTree, searchTerm);
                  const checkState = getCategoryCheckState(
                    focusedStudyId,
                    focusedSiteId,
                    category,
                  );
                  const checkedIds =
                    externalCheckedFolders?.[searchKey] ||
                    (checkState.checked && !checkState.indeterminate
                      ? collectFolderNodeIds(fullTree)
                      : []);

                  const catKey = categoryNodeKey(
                    focusedStudyId,
                    focusedSiteId,
                    category,
                  );
                  const categoryTree = [
                    {
                      id: catKey,
                      name: category,
                      type: "folder",
                      children: filteredTree,
                    },
                  ];
                  const treeCheckedIds =
                    checkState.checked && !checkState.indeterminate
                      ? [...checkedIds, catKey]
                      : checkedIds;

                  return (
                    <div
                      key={category}
                      className="external-grant-category"
                    >
                      {externalStudyLoading?.[cacheKey] || !hasTreeKey ? (
                        <div className="text-muted small">
                          Loading {category} folders…
                        </div>
                      ) : fullTree.length === 0 ? (
                        <div className="text-muted small">
                          No {category} folders at this site.
                        </div>
                      ) : (
                        <CheckboxTreeView
                          data={categoryTree}
                          checkboxType="folder"
                          checkedIds={treeCheckedIds}
                          cascadeFolders={true}
                          showIndeterminate
                          disabled={readOnly}
                          defaultExpanded
                          onChange={(checked) => {
                            if (readOnly) return;
                            setExternalCheckedFolders((prev) => ({
                              ...prev,
                              [searchKey]: (checked || []).filter(
                                (id) => String(id) !== String(catKey),
                              ),
                            }));
                          }}
                        />
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="external-grant-col external-grant-col--selected">
            <div className="external-grant-col__head">
              <span>Selected</span>
              <span className="external-studies-assign__count">
                {selectionSummary.length
                  ? `${selectionSummary.length} stud${
                      selectionSummary.length === 1 ? "y" : "ies"
                    }`
                  : "None"}
              </span>
            </div>
            <div className="external-grant-col__body external-grant-col__body--selected">
              {selectionSummary.length === 0 ? (
                <div className="text-muted small">
                  Nothing selected yet. Choose a study, site and folders to see
                  them here.
                </div>
              ) : (
                selectionSummary.map((item) => (
                  <div
                    key={String(item.studyId)}
                    className="external-assign-selected__card"
                  >
                    <div className="external-assign-selected__study">
                      <span
                        className="external-assign-selected__study-name"
                        title={item.studyLabel}
                      >
                        {item.studyLabel}
                      </span>
                      {!readOnly && !assignLockStudyId ? (
                        <button
                          type="button"
                          className="external-assign-selected__remove"
                          title="Remove selection"
                          aria-label="Remove selection"
                          onClick={(e) => {
                            e.preventDefault();
                            unshareStudyRow(e, {
                              value: item.studyId,
                              label: item.studyLabel,
                            });
                          }}
                        >
                          <i className="fas fa-trash-alt" aria-hidden="true" />
                        </button>
                      ) : null}
                    </div>
                    <div className="external-assign-selected__until">
                      Visible until: {item.untilLabel}
                    </div>
                    <ul className="external-assign-selected__sites">
                      {item.siteBlocks.map((site) => (
                        <li key={String(site.siteId)}>
                          <span className="external-assign-selected__site-name">
                            {site.siteName}
                          </span>
                          <ul className="external-assign-selected__cats">
                            {(site.cats || []).length ? (
                              site.cats.map((cat) => (
                                <li key={cat.category}>
                                  {cat.category}
                                  {cat.whole
                                    ? " · all folders"
                                    : ` · ${cat.folders}`}
                                </li>
                              ))
                            ) : (
                              <li className="text-muted">Folders pending…</li>
                            )}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const formatVisibleUntil = (raw) => {
    const text = String(raw || "").trim();
    if (!text) return "Unlimited";
    return formatOrgWallClockDisplay(text) || "Unlimited";
  };

  const splitStudyDisplay = (study) => {
    const number = String(
      study?.studyNumber || study?.StudyNumber || study?.StudyCode || "",
    ).trim();
    const name = String(
      study?.studyName || study?.StudyName || "",
    ).trim();
    const label = String(study?.label || "").trim();
    if (number || name) {
      return {
        number: number || "—",
        name: name || label || "—",
      };
    }
    const dash = label.match(/^(.*?)\s+-\s+(.*)$/);
    if (dash) {
      return {
        number: dash[1].trim() || "—",
        name: dash[2].trim() || "—",
      };
    }
    return { number: "—", name: label || "—" };
  };

  const grantStatusLabel = (accessUntil) => {
    const text = String(accessUntil || "").trim();
    if (!text) return "Active";
    return isOrgWallClockPast(text, orgTimeZoneId) ? "Expired" : "Active";
  };

  const getAssignedStudyReportRows = useCallback(() => {
    const grantAudit = new Map();
    (parseExternalEntityAccessRows(serverEntityAccess) || []).forEach((row) => {
      const key = `${row.studyId}|${row.siteId}|${row.category}`.toLowerCase();
      if (!grantAudit.has(key)) {
        grantAudit.set(key, {
          createdBy: row.createdBy || "",
          createdByName: collapsePersonName(row.createdByName),
          createdOn: row.createdOn || "",
        });
      }
    });
    const rows = [];
    (studies || []).forEach((study) => {
      const studyId = study?.StudyId || study?.value;
      if (!studyId) return;
      const display = splitStudyDisplay(study);
      const label =
        study?.label ||
        (display.number !== "—"
          ? `${display.number} - ${display.name}`
          : display.name);
      const studyColor =
        study?.studyColor || study?.StudySchColor || study?.StudyColor || "";
      const siteIds = uniqueSiteIds(
        getStudyScope(externalStudyScopeByStudyId, studyId)?.siteIds || [],
      );
      const siteOptions = getStudySiteOptions(studySitesByStudyId, studyId);
      const accessUntil = normalizeAccessUntilForSave(
        getStudyScope(externalStudyScopeByStudyId, studyId),
      );
      const visibleUntil = formatVisibleUntil(accessUntil);
      const status = grantStatusLabel(accessUntil);
      const studyCats = categoriesForStudy(studyId);

      const folderCounts = (siteId, category) => {
        const key = folderCheckKey(studyId, siteId, category);
        const tree = getSiteFolderTree(
          externalStudyTrees,
          studyId,
          siteId,
          category,
        );
        const total = collectFolderNodeIds(tree).length;
        const hasKey = Object.prototype.hasOwnProperty.call(
          externalCheckedFolders || {},
          key,
        );
        const checks = externalCheckedFolders?.[key] || [];
        const folderIds = collectFolderIdsFromCheckedIds(checks);
        const whole = !hasKey || idsCoverWholeTree(tree, checks);
        const assigned = whole ? total : folderIds.length;
        return {
          assigned,
          total,
          whole,
          folders: total ? `${assigned} / ${total}` : "—",
          scope: !total ? "—" : whole ? "All folders" : "Selected folders",
        };
      };

      const categoriesForSite = (siteId) => {
        return studyCats.filter((category) =>
          hasFolderCheckKey(
            externalCheckedFolders || {},
            studyId,
            siteId,
            category,
          ),
        );
      };

      const pushRow = ({ siteId, siteName, category }) => {
        const counts = siteId
          ? folderCounts(siteId, category)
          : {
              assigned: 0,
              total: 0,
              whole: false,
              folders: "—",
              scope: "—",
            };
        const audit = findGrantAudit(
          grantAudit,
          studyId,
          siteId,
          category || "eISF",
        );
        const assignedBy = collapsePersonName(audit.createdByName) || "—";
        const assignedOn = formatAssignedOnDate(audit.createdOn) || "—";
        rows.push({
          id: `${studyId}::${siteId || "none"}::${category || "eISF"}`,
          StudyId: studyId,
          Study: label,
          StudyName: display.name,
          StudyNumber: display.number,
          StudyColor: studyColor,
          SiteId: siteId || "",
          Sites: siteName || "No sites",
          SiteIds: siteId ? [siteId] : [],
          SiteNames: siteName ? [siteName] : [],
          Folders: counts.folders,
          AssignedFolders: counts.assigned,
          TotalFolders: counts.total,
          Category: category || "eISF",
          Categories: [category || "eISF"],
          Status: status,
          VisibleUntil: visibleUntil,
          AccessUntil: accessUntil,
          AssignedBy: assignedBy,
          AssignedOn: assignedOn,
          AssignedOnRaw: audit.createdOn || "",
          CreatedBy: assignedBy,
          CreatedOn: assignedOn,
        });
      };

      if (!siteIds.length) {
        // Grants may still have site/category rows even if scope hydration
        // has not populated siteIds yet (or whole-study grants use empty SiteId).
        const grantFallback = (
          parseExternalEntityAccessRows(serverEntityAccess) || []
        ).filter(
          (row) =>
            String(row?.studyId || "")
              .trim()
              .toLowerCase() === String(studyId).trim().toLowerCase(),
        );
        if (!grantFallback.length) return;

        const seen = new Set();
        grantFallback.forEach((row) => {
          const siteId = String(row.siteId || "").trim();
          const category = String(row.category || "eISF").trim() || "eISF";
          const dedupe = `${siteId}::${category}`.toLowerCase();
          if (seen.has(dedupe)) return;
          seen.add(dedupe);
          const siteName = siteId
            ? resolveAssignedSiteName(siteId, studySitesByStudyId, studyId) ||
              "Site"
            : "All sites";
          pushRow({
            siteId,
            siteName,
            category,
          });
        });
        return;
      }

      siteIds.forEach((siteId) => {
        const siteName =
          resolveAssignedSiteName(siteId, studySitesByStudyId, studyId) ||
          (siteOptions.length ? "Site" : "");
        categoriesForSite(siteId).forEach((category) => {
          pushRow({ siteId, siteName: siteName || "Site", category });
        });
      });
    });
    rows.sort((a, b) => {
      const byDate =
        assignedOnSortMs(b.AssignedOnRaw) - assignedOnSortMs(a.AssignedOnRaw);
      if (byDate) return byDate;
      const byStudy = String(a.Study || "").localeCompare(
        String(b.Study || ""),
        undefined,
        { sensitivity: "base" },
      );
      if (byStudy) return byStudy;
      const bySite = String(a.Sites || "").localeCompare(
        String(b.Sites || ""),
        undefined,
        { sensitivity: "base" },
      );
      if (bySite) return bySite;
      return String(a.Category || "").localeCompare(
        String(b.Category || ""),
        undefined,
        { sensitivity: "base" },
      );
    });
    return rows;
  }, [
    studies,
    externalStudyScopeByStudyId,
    studySitesByStudyId,
    externalStudyTrees,
    externalCheckedFolders,
    categoriesForStudy,
    serverEntityAccess,
  ]);

  const cloneScopeMap = (scope) => {
    const next = {};
    Object.entries(scope || {}).forEach(([key, value]) => {
      next[key] = {
        ...(value || {}),
        siteIds: [...(value?.siteIds || [])],
      };
    });
    return next;
  };

  const beginAssignSession = useCallback(
    (studyId, extras = {}) => {
      assignSnapshotRef.current = {
        studies: [...(studiesRef.current || [])],
        scope: cloneScopeMap(externalStudyScopeByStudyId),
        checked: { ...(externalCheckedFolders || {}) },
        activeStudyId,
        activeSiteId,
      };
      setAssignSessionActive(true);
      setAssignFolderProbeDoneByStudy({});
      const lockId = String(studyId || "").trim();
      setAssignLockStudyId(lockId || null);
      setStudyListQuery("");
      setSiteListQuery("");
      if (lockId) {
        const siteIds = uniqueSiteIds(
          getStudyScope(externalStudyScopeByStudyId, lockId)?.siteIds || [],
        );
        const requested = String(extras?.siteId || "").trim();
        const firstSite =
          (requested &&
            siteIds.find(
              (id) =>
                String(id).toLowerCase() === requested.toLowerCase(),
            )) ||
          siteIds[0] ||
          null;
        setActiveStudyId(lockId);
        setActiveSiteId(firstSite);
        setExpandedKeys((prev) => ({
          ...prev,
          [studyNodeKey(lockId)]: true,
          ...(firstSite ? { [siteNodeKey(lockId, firstSite)]: true } : {}),
        }));
        loadStudySites(lockId);
      } else {
        // New assign: open with no study/site pre-selected.
        setActiveStudyId(null);
        setActiveSiteId(null);
      }
    },
    [
      externalStudyScopeByStudyId,
      externalCheckedFolders,
      activeStudyId,
      activeSiteId,
      loadStudySites,
    ],
  );

  const cancelAssignSession = useCallback(() => {
    const snap = assignSnapshotRef.current;
    if (!snap) return;
    onStudiesChange(snap.studies);
    setExternalStudyScopeByStudyId(snap.scope);
    setExternalCheckedFolders(snap.checked);
    setActiveStudyId(snap.activeStudyId || null);
    setActiveSiteId(snap.activeSiteId || null);
    setAssignLockStudyId(null);
    setAssignSessionActive(false);
    setAssignFolderProbeDoneByStudy({});
    assignSnapshotRef.current = null;
  }, [onStudiesChange]);

  const commitAssignSession = useCallback(() => {
    setAssignLockStudyId(null);
    setAssignSessionActive(false);
    setAssignFolderProbeDoneByStudy({});
    assignSnapshotRef.current = null;
  }, []);

  const applyShareSnapshot = useCallback(
    (nextStudies, nextScope, nextChecked) => {
      studiesRef.current = nextStudies;
      scopeRef.current = nextScope;
      checkedRef.current = nextChecked;
      onStudiesChange(nextStudies);
      setExternalStudyScopeByStudyId(nextScope);
      setExternalCheckedFolders(nextChecked);
    },
    [onStudiesChange],
  );

  const clearStudyChrome = useCallback((studyId) => {
    setExpandedKeys((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (
          key === studyNodeKey(studyId) ||
          key.startsWith(`site:${studyId}::`) ||
          key.startsWith(`cat:${studyId}::`)
        ) {
          delete next[key];
        }
      });
      return next;
    });
    if (sameShareId(activeStudyId, studyId)) {
      setActiveStudyId(null);
      setActiveSiteId(null);
    }
  }, [activeStudyId]);

  const unshareAssignedStudy = useCallback(
    (studyId) => {
      const dropped = dropStudyFromShareMaps(
        studiesRef.current,
        scopeRef.current,
        checkedRef.current,
        studyId,
      );
      applyShareSnapshot(
        dropped.nextStudies,
        dropped.nextScope,
        dropped.nextChecked,
      );
      clearStudyChrome(studyId);
      return dropped.nextStudies;
    },
    [applyShareSnapshot, clearStudyChrome],
  );

  const unshareAssignedGrant = useCallback(
    (row = {}) => {
      const sid = String(row.studyId || row.StudyId || "").trim();
      if (!sid) return studiesRef.current || [];
      const site = String(row.siteId || row.SiteId || "").trim();
      const category = row.category || row.Category || "";
      if (!site) return unshareAssignedStudy(sid);

      const currentStudies = studiesRef.current || [];
      const currentScope = scopeRef.current || {};
      const nextChecked = { ...(checkedRef.current || {}) };
      Object.keys(nextChecked).forEach((key) => {
        const parts = String(key).split("::");
        if (
          parts.length >= 3 &&
          sameShareId(parts[0], sid) &&
          sameShareId(parts[1], site) &&
          (!category || sameShareId(parts[2], category))
        ) {
          delete nextChecked[key];
        }
      });

      const remainingCats = categoriesForStudy(sid).filter((cat) => {
        if (category && sameShareId(cat, category)) return false;
        return hasFolderCheckKey(nextChecked, sid, site, cat);
      });

      if (remainingCats.length === 0) {
        const afterSite = removeSiteFromShareMaps(
          currentScope,
          nextChecked,
          sid,
          site,
        );
        const leftoverSites = uniqueSiteIds(
          getStudyScope(afterSite.nextScope, sid)?.siteIds || [],
        );
        if (leftoverSites.length === 0) {
          return unshareAssignedStudy(sid);
        }
        applyShareSnapshot(
          currentStudies,
          afterSite.nextScope,
          afterSite.nextChecked,
        );
        return currentStudies;
      }

      applyShareSnapshot(currentStudies, currentScope, nextChecked);
      return currentStudies;
    },
    [applyShareSnapshot, categoriesForStudy, unshareAssignedStudy],
  );

  const assignedSitesPending = (studies || []).some((row) => {
    const studyId = row?.StudyId || row?.value;
    if (!studyId) return false;
    const idKey = String(studyId).trim().toLowerCase();
    // Only block while a site fetch is actively in progress for this study.
    return Object.entries(studySitesLoadingById || {}).some(
      ([key, loading]) =>
        Boolean(loading) && String(key).trim().toLowerCase() === idKey,
    );
  });
  const hydrationPending =
    hydrationNonce > 0 &&
    hydrationNonce !== lastHydrationCompletedNonceRef.current &&
    shareHydrationBusy;
  const assignedTableLoading = Boolean(
    tableBootstrapping ||
      hydrationPending ||
      shareHydrationBusy ||
      assignedSitesPending,
  );

  return {
    reset,
    buildCroDocumentListString,
    buildExternalStaffIsfDocumentsPayload,
    buildAssignDeltaDocumentRows,
    getStudiesMissingSites,
    getStudyShareValidationError,
    assignedTableLoading,
    getAssignedStudyReportRows,
    beginAssignSession,
    cancelAssignSession,
    commitAssignSession,
    unshareAssignedStudy,
    unshareAssignedGrant,
    focusStudy,
    renderStudiesStep,
  };
}

/** Presentational wrapper if you prefer a component in JSX trees. */
export default function ExternalUserAccessScoping(props) {
  const { renderStudiesStep } = useExternalUserAccessScoping(props);
  return renderStudiesStep();
}
